import { api, NotifyModelKey, CommentRevisionHint, SubjectNameRevision } from "./api";
import { Post } from "./posts";
import { showStars, trackUnsavedInput, STAR_RATING_LABELS, EXAM_RATING_LABELS, normalizeForSearch, attachDropdownKeyboardNav, WOULD_TAKE_AGAIN_ENABLED, Result, renderEaseOfPassingText, EASE_OF_PASSING_ENABLED } from "./generics";
import { renderPostHocNotifyPrompt } from "./notifyEmail";
import { renderDifficultyRating } from "./subjects";
// "Winorośl" wokół pola komentarza jest na razie wyłączona (patrz vine.ts) - kod zostaje
// nietknięty na wypadek, gdyby trzeba było ją z powrotem włączyć.
// import { attachVine } from "./vine";


// Data
export type subjectStatus = "active" | "rejected" | "awaiting_moderation";

export interface Subject {
    id: number;
    name: string;
    status: subjectStatus;
}

// Kandydat wydziału do pickera w formularzu komentarza (patrz renderFacultyPicker niżej) - lżejszy
// niż pełny Faculty z posts.ts (bez lecturer_count, który tu i tak nie jest potrzebny/dociągany).
export interface FacultyCandidate {
    id: number;
    name: string;
    slug: string;
}

// Wydział-zbiornik z importu ogólnopolskiej bazy naukowców (patrz PLACEHOLDER_FACULTY_SLUG w
// serializers.py) - ten sam wzorzec co w landing2.ts/addLecturer.ts: nigdy nie jest prawdziwym
// wyborem w pickerze (patrz renderFacultyPicker), filtrujemy go z pełnej listy wydziałów uczelni.
const PLACEHOLDER_FACULTY_SLUG = "bez-wydzialu";

export interface Comment {
    // rating (zajęcia) jest nullable - opinia zaimportowana z wpia-wiki.pl może oceniać
    // WYŁĄCZNIE egzamin (patrz Comment.rating w models.py) - formularz na żywo (initCommentForm
    // niżej) nadal wymaga tego pola zawsze, więc null trafia się tylko w danych z importu.
    id: number; body: string; created: string; active: boolean, rating: number | null,
    upvotes: number, downvotes: number, status: commentStatus; subject: Subject | null;
    would_take_again: boolean | null; is_from_psych_import: boolean; is_from_wpia_wiki: boolean;
    is_from_filozofia_wiki: boolean;
    // Ustawiane WYŁĄCZNIE, gdy AI faktycznie odrzuciła treść (nie przy awarii serwera AI - patrz
    // PostCommentView.create w comments.py) - używane niżej do odróżnienia komunikatu "AI wykryła
    // problem" od zwykłego "czeka na moderację".
    ai_rejection_reason: string;
    // Druga, opcjonalna ocena - egzamin (patrz Comment.exam_* w models.py) - gwiazdki tak jak
    // rating wyżej, ale osobne etykiety (patrz EXAM_RATING_LABELS w generics.ts - "Wybitny" nie
    // pasuje do egzaminu). Zbierana przez formularz tylko na wydziałach z
    // supports_exam_dimensions (patrz CommentFormOptions.supportsExamDimensions niżej), ale karta
    // komentarza pokazuje ją zawsze, gdy jest obecna (harmless na innych wydziałach - tam zawsze
    // null). exam_atmosphere nie jest dziś zbierane przez żaden formularz (tylko import AI z
    // wpia-wiki.pl), ale serializer (exclude=['ip_address']) je i tak wysyła, więc jest tu
    // zadeklarowane - potrzebne do rozpoznania "czy ta opinia w ogóle dotyczy egzaminu" (patrz
    // filterCommentsByDimension w post_detail.ts/compareView.ts - musi sprawdzać KAŻDY wymiar
    // egzaminu, nie tylko exam_rating). exam_difficulty - patrz class_difficulty niżej, ten sam
    // formularz od teraz zbiera oba.
    exam_rating: number | null; exam_atmosphere: number | null;
    exam_difficulty: number | null; exam_would_take_again: boolean | null; exam_body: string;
    // Trudność zajęć/egzaminu jako zwykły tekst, nie gwiazdki (patrz renderEaseOfPassingText w
    // generics.ts, DIFFICULTY_LABELS/renderDifficultyRating w subjects.ts dla samego widgetu
    // wyboru) - obie kolumny zbierane teraz przez initCommentForm niżej (RMP-style: pokazuje się
    // tylko liczba, bez rozkładu - dwa rozkłady ocen na jednej stronie obok gwiazdek zajęć/
    // egzaminu byłyby nieczytelne).
    class_difficulty: number | null;
}

export interface CommentGroup {
    subject: Subject | null; // null = grupa "Ogólne"
    comments: Comment[];
}

// Etykieta dla komentarzy bez wybranego przedmiotu - używana zarówno jako nagłówek grupy,
// jak i jako jawna, wybieralna pozycja na liście przedmiotów (obok pozostawienia pola pustym).
export const GENERAL_SUBJECT_LABEL = "Komentarz ogólny";

export const COMMENT_BODY_MAX_LENGTH = 500;


// Id tymczasowego, jeszcze niezapisanego komentarza (patrz optimistic UI w initCommentForm) -
// zawsze ujemne, więc nigdy nie koliduje z prawdziwym id z bazy (te są dodatnie). Malejący
// licznik modułu (nie Date.now()) - gwarantuje unikalność nawet przy dwóch wysyłkach w tej samej
// milisekundzie (np. główny formularz i szybki formularz przy przedmiocie na tej samej stronie).
let nextOptimisticCommentId = -1;

// Z czym w regulaminie/RODO jest niezgodny fragment wskazany przez moderację AI (kategorie z
// evaluate_comment w pages/api/moderation.py, patrz REVISION_CATEGORIES w pages/api/comments.py).
// Stały tekst, nie generowany przez AI - na wyraźną prośbę: autor ma wiedzieć, że ograniczenie
// wynika z regulaminu i prawa, a nie z tego, że AI "nie spodobała się" jego opinia.
const REVISION_RULES: { [category: string]: string } = {
    RODO_ART9: "Regulamin (§3 ust. 3) zabrania ujawniania danych prywatnych, a RODO (art. 9) szczególnie "
        + "chroni informacje o zdrowiu, poglądach politycznych, wyznaniu, orientacji czy pochodzeniu "
        + "konkretnej osoby. Nie możemy ich publikować, nawet jeśli są prawdziwe.",
    WULGARYZM: "Regulamin (§3 ust. 3) zabrania wulgaryzmów i obraźliwych określeń. Twoja krytyka może "
        + "zostać, chodzi tylko o formę.",
    // Celowo zaczyna się od "krytyka jest w porządku" - problemem jest gotowa etykieta zarzutu
    // postawiona jako fakt, nie sama krytyka (na wyraźną prośbę 2026-10-15).
    OSKARZENIE_O_NIEUCZCIWOSC: "Krytyka prowadzącego jest w porządku. Regulamin (§3 ust. 3) zabrania jednak "
        + "pomówień, dlatego wprost postawione poważne zarzuty (np. korupcji, molestowania czy dyskryminacji) "
        + "przed publikacją sprawdza moderator.",
    NIE_NA_TEMAT: "Regulamin (§3 ust. 3) zabrania spamu, reklam i treści niezwiązanych z oceną zajęć i prowadzących.",
};
const REVISION_RULE_FALLBACK = "Ten fragment może być niezgodny z regulaminem (§3 ust. 3).";

// Podpowiedź od AI to sama końcówka zdania w bezokoliczniku ("zamienić „kurwa” na łagodniejsze
// słowo") - stały początek dokleja serwis, żeby forma była zawsze ta sama i nie brzmiała jak
// rozkaz (na wyraźną prośbę 2026-10-15). Zabezpieczenie na wypadek, gdyby AI zaczęło wielką literą
// albo postawiło kropkę.
function formatRevisionFix(fix: string): string {
    let phrase = fix.trim().replace(/[.!]+$/, "");
    if (/^\p{Lu}\p{Ll}/u.test(phrase)) phrase = phrase.charAt(0).toLowerCase() + phrase.slice(1);
    return `Aby opinia mogła zostać opublikowana, wystarczy ${phrase}.`;
}

// Ikona łapki głosowania - kształt "kciuka" (w stylu FB), rysowany przez stroke="currentColor",
// żeby dziedziczył kolor przycisku (var(--text-color) domyślnie, var(--primary-color) po
// zagłosowaniu - patrz .comment .active-vote w CSS) zamiast sztywnego (i tu akurat żółtego)
// koloru emoji 👍/👎. Łapka "w dół" to ta sama ikona odbita w pionie (.vote-icon-down w CSS) -
// CAŁY narysowany kształt (nie tylko mankiet dłoni) jest wyśrodkowany w pionie w viewBox (bbox
// y:3.65-20.35, środek=12), więc po odbiciu zajmuje dokładnie ten sam zakres wysokości co w
// łapce "w górę", zamiast tylko mankietu przy nieporównanej reszcie kształtu.
const VOTE_ICON_PATH = `<path d="M3 11.85 A2.2 2.2 0 0 1 5.2 9.65 H6.8 A2.2 2.2 0 0 1 9 11.85 V17.45 A2.2 2.2 0 0 1 6.8 19.65 H5.2 A2.2 2.2 0 0 1 3 17.45 Z"/><path d="M9 11.85 L9 17.45 L10.3 19.25 C11.1 19.95 12.2 20.35 13.3 20.35 H17.5 C18.6 20.35 19.5 19.55 19.7 18.45 L20.9 12.25 C21.1 11.05 20.2 9.95 19 9.95 H14.8 C15.3 8.65 15.6 7.25 15.6 5.85 C15.6 4.65 14.6 3.65 13.4 3.65 C12.9 3.65 12.5 3.95 12.4 4.45 L11.3 8.15 C11 9.15 10.3 10.05 9.4 10.65 Z"/>`;
const UPVOTE_ICON_SVG = `<svg class="vote-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${VOTE_ICON_PATH}</svg>`;
const DOWNVOTE_ICON_SVG = `<svg class="vote-icon vote-icon-down" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${VOTE_ICON_PATH}</svg>`;

export type commentStatus = "active" | "rejected" | "awaiting_moderation" | "default_comment_state" | "reported"
// best_exam_rating/worst_exam_rating - odpowiednik best_rating/worst_rating dla exam_rating,
// używane w porównaniu (compareView.ts) na zakładce "Egzamin" (patrz DimensionTab w
// post_detail.ts - tu bez osobnego typu, bo compareView nie ma zakładki "Pytania").
export type CommentSortField  =
    "best_rating" | "worst_rating" | "best_exam_rating" | "worst_exam_rating"
    | "newest" | "oldest" | "most_upvotes" | "by_subject";
export type Votetype = "upvote" | "downvote" | "no-vote"
type ValidationResult =
    | { valid: true }
    | { valid: false; errors: Record<string, string> };

// Powody zgłoszenia komentarza - te same kategorie zakazanych treści co rules.html §3.3.
// Wartości ("value") muszą zgadzać się z CommentReport.REASON_CHOICES w backendzie.
const REPORT_REASONS: { value: string; label: string }[] = [
    { value: "personal_rights", label: "Narusza dobra osobiste (zniesławienie, pomówienie)" },
    { value: "hate_vulgar", label: "Wulgaryzmy, mowa nienawiści, treści dyskryminujące" },
    { value: "private_data", label: "Ujawnia dane prywatne" },
    { value: "spam", label: "Spam lub reklama" },
    { value: "off_topic", label: "Nie dotyczy tematyki Serwisu" },
    { value: "other", label: "Inne" },
];




// Functions

const POLISH_MONTH_ABBREVIATIONS = [
    "Sty", "Lut", "Mar", "Kwi", "Maj", "Cze", "Lip", "Sie", "Wrz", "Paź", "Lis", "Gru",
];

// Komentarze zaimportowane z archiwum społeczności Fandom MIMUW noszą tę samą datę importu -
// oznaczamy je odznaką źródła (patrz renderFandomSourceBadge) zamiast zmieniać wyświetlaną datę.
const FANDOM_IMPORT_DATE = { day: 21, month: 6, year: 2025 }; // month: 0 = styczeń, więc 6 = lipiec
const FANDOM_ATTRIBUTION = "Źródło: Archiwum społeczności Fandom MIMUW / Licencja: CC BY-SA 3.0. "
    + "Gwiazdki oraz poleca/odradza (lub brak w przypadku niejednoznacznej opinii) zostały przypisane za pomocą narzędzi AI według obowiązującej skali.";

export function isFandomImportedComment(comment: Comment): boolean {
    const createdDate = new Date(comment.created);
    return createdDate.getDate() === FANDOM_IMPORT_DATE.day
        && createdDate.getMonth() === FANDOM_IMPORT_DATE.month
        && createdDate.getFullYear() === FANDOM_IMPORT_DATE.year;
}

// Odznaka źródła dla komentarzy z importu Fandom - używana w widoku porównania (patrz
// compareView), gdzie stoi obok gwiazdek w jednej linii z datą. W głównym widoku komentarza
// (patrz renderCommentCorner niżej) zajmuje zamiast tego samo miejsce daty w rogu karty.
export function renderFandomSourceBadge(): HTMLElement {
    return renderSourceBadge(FANDOM_ATTRIBUTION);
}

// Atrybucja dla komentarzy z importu bazy "Wykładowcy Psych" (patrz SOURCE_LABEL w
// import_psych_reviews.py - ten sam tekst, tylko tam trafiał kiedyś wprost do treści komentarza;
// teraz sygnalizowany odznaką źródła jak przy Fandom wyżej, zamiast tekstem pod każdą opinią).
const PSYCH_ATTRIBUTION = "Źródło: baza „Wykładowcy Psych” (zbiorczy dokument opinii studentów "
    + "Wydziału Psychologii UW, udostępniony za zgodą). Ocena gwiazdkowa przyznana automatycznie "
    + "przez AI na podstawie treści opinii, nie wybrana przez jej autora/autorkę.";

export function renderPsychSourceBadge(): HTMLElement {
    return renderSourceBadge(PSYCH_ATTRIBUTION);
}

// Atrybucja dla komentarzy z importu wpia-wiki.pl (wiki społeczności Wydziału Prawa i
// Administracji UW, treść na licencji Public Domain) - ten sam wzorzec odznaki co Fandom/Psych
// wyżej (patrz import_wpia_wiki.py).
// Import z wpia-wiki.pl przypisywał przez AI więcej niż same gwiazdki (patrz prompt w
// import_wpia_wiki.py: rating/atmosphere/difficulty/would_take_again, osobno dla zajęć i egzaminu) -
// dymek mówi więc wprost także o "poleca/odradza" i podziale zajęcia/egzamin (na wyraźną prośbę).
const WPIA_WIKI_ATTRIBUTION = "Źródło: wpia-wiki.pl (wiki społeczności Wydziału Prawa i Administracji UW, "
    + "licencja Public Domain). Ocena gwiazdkowa, poleca/odradza, trudność oraz podział na zajęcia i egzamin "
    + "zostały przypisane automatycznie przez AI na podstawie treści opinii, nie wybrane przez jej autora/autorkę.";

export function renderWpiaWikiSourceBadge(): HTMLElement {
    return renderSourceBadge(WPIA_WIKI_ATTRIBUTION);
}

// Atrybucja dla komentarzy z importu filozofiauw.wikidot.com (wiki społeczności Wydziału
// Filozofii UW, licencja Creative Commons BY-SA 3.0) - ten sam wzorzec odznaki co wyżej.
const FILOZOFIA_WIKI_ATTRIBUTION = "Źródło: filozofiauw.wikidot.com (wiki społeczności Wydziału Filozofii UW, "
    + "licencja Creative Commons BY-SA 3.0). Ocena gwiazdkowa przyznana automatycznie przez AI na podstawie "
    + "treści opinii, nie wybrana przez jej autora/autorkę.";

export function renderFilozofiaWikiSourceBadge(): HTMLElement {
    return renderSourceBadge(FILOZOFIA_WIKI_ATTRIBUTION);
}

// Dyskretne kółko z "i" (jak ikona informacji) w rogu karty komentarza, z tooltipem
// pokazującym pełną atrybucję po najechaniu/fokusie (patrz .comment-source-badge w CSS). Ta sama
// litera dla WSZYSTKICH źródeł (dawniej osobna litera na źródło: F/P/W/I) - na wyraźną prośbę,
// bo pojedyncza, źródło-specyficzna litera nie mówiła sama z siebie, że w ogóle jest tam coś do
// odczytania; jednolite "i" jest od razu czytelne jako "tu jest dodatkowa informacja", a samo
// źródło i tak poznaje się dopiero z treści dymka, nie z litery.
function renderSourceBadge(attribution: string): HTMLElement {
    const badge = document.createElement("span");
    badge.className = "comment-source-badge";
    // Wektorowe "i" (kropka + trzon jako osobne kształty), NIE tekstowy znak "i" - przy 14px
    // kółku i pogrubionej wadze czcionki kropka zlewała się z trzonem w nieczytelną plamkę
    // (zgłoszenie użytkownika). SVG gwarantuje stałą, widoczną przerwę między nimi niezależnie
    // od fontu/hintingu przeglądarki. fill="currentColor" - dziedziczy `color` z CSS (ten sam
    // jasny kolor na ciemnym tle kółka, co miał tekstowy znak).
    badge.innerHTML = `<svg viewBox="0 0 8 11" width="8" height="11" fill="currentColor" aria-hidden="true">`
        + `<circle cx="4" cy="1.6" r="1.6"/><rect x="2.8" y="4.4" width="2.4" height="6.6" rx="1.2"/></svg>`;
    // Nie natywny title (przeglądarka pokazuje go dopiero po ~1-2s) - data-tooltip + CSS
    // (patrz .comment-source-badge::after) pokazuje się od razu po najechaniu. aria-label
    // zamiast title zachowuje dostępność dla czytników ekranu bez podwójnego dymka.
    badge.setAttribute("data-tooltip", attribution);
    badge.setAttribute("aria-label", attribution);
    return badge;
}

// Pigułka "poleca/odradza ponowny wybór" w nagłówku POJEDYNCZEJ opinii, obok gwiazdek (patrz
// .comment-meta w renderComment niżej) - osobna od zagregowanego "N% wybrałoby ponownie" na
// profilu wykładowcy (patrz renderWouldTakeAgainBadge w posts.ts, to samo pole, ale uśredniony
// procent). null (brak jednoznacznej odpowiedzi w opinii - patrz would_take_again w modelu) nie
// pokazuje nic, zamiast zgadywać.
function renderCommentWouldTakeAgainBadge(comment: Comment): HTMLElement | null {
    if (!WOULD_TAKE_AGAIN_ENABLED) return null;
    if (comment.would_take_again === null) return null;

    const badge = document.createElement("span");
    badge.className = "comment-would-take-again-badge";
    badge.classList.add(comment.would_take_again ? "would-take-again-yes" : "would-take-again-no");
    badge.textContent = comment.would_take_again ? "\u{1F44D} Poleca" : "\u{1F44E} Odradza";
    badge.title = comment.would_take_again
        ? "Autor(ka) poleciłby/poleciłaby wybór tego wykładowcy ponownie"
        : "Autor(ka) odradza ponowny wybór tego wykładowcy";
    return badge;
}

// Odpowiednik renderCommentWouldTakeAgainBadge wyżej, dla wymiaru egzaminu (Comment.
// exam_would_take_again) - osobny wariant tekstu/tooltipu, żeby nie mylić z poleceniem dot.
// samych zajęć, gdy oba są obecne na tej samej karcie.
function renderExamWouldTakeAgainBadge(comment: Comment): HTMLElement | null {
    if (!WOULD_TAKE_AGAIN_ENABLED) return null;
    if (comment.exam_would_take_again === null) return null;

    const badge = document.createElement("span");
    badge.className = "comment-would-take-again-badge";
    badge.classList.add(comment.exam_would_take_again ? "would-take-again-yes" : "would-take-again-no");
    badge.textContent = comment.exam_would_take_again ? "\u{1F44D} Poleca" : "\u{1F44E} Odradza";
    badge.title = comment.exam_would_take_again
        ? "Autor(ka) poleciłby/poleciłaby ponownie ten egzamin"
        : "Autor(ka) odradza ponowne podejście do tego egzaminu";
    return badge;
}

// Data dodania komentarza - zawsze pełna data (np. "12 Mar 2026"), nie względna ("3 dni temu").
// Celowo dyskretna - dodawana w rogu karty (patrz renderComment), nie w głównym nagłówku.
export function whenAdded(comment: Comment): HTMLElement {
    const whenAddedContainer = document.createElement("div");
    whenAddedContainer.className = "comment-date";

    const createdDate = new Date(comment.created);
    const day = createdDate.getDate();
    const month = POLISH_MONTH_ABBREVIATIONS[createdDate.getMonth()];
    const year = createdDate.getFullYear();

    whenAddedContainer.textContent = `${day} ${month} ${year}`;
    return whenAddedContainer;
}

// Rożek karty komentarza (prawy górny róg - nad menu "⋮", w linii z gwiazdkami, patrz
// .comment-date w CSS) - normalnie data dodania, ale dla komentarzy z importu Fandom sama data
// nic nie mówi (wszystkie mają identyczną, dzień importu - patrz FANDOM_IMPORT_DATE), a
// komentarze z importu Psych (patrz is_from_psych_import) w ogóle nie mają sensownego znaczenia
// daty dodania (wszystkie dodane hurtowo tego samego dnia co import), więc w tym samym miejscu
// pokazujemy zamiast niej odznakę źródła (patrz renderFandomSourceBadge/renderPsychSourceBadge).
function renderCommentCorner(comment: Comment): HTMLElement {
    if (isFandomImportedComment(comment) || comment.is_from_psych_import || comment.is_from_wpia_wiki
        || comment.is_from_filozofia_wiki) {
        const container = document.createElement("div");
        container.className = "comment-date";
        let badge: HTMLElement;
        if (comment.is_from_psych_import) {
            badge = renderPsychSourceBadge();
        } else if (comment.is_from_wpia_wiki) {
            badge = renderWpiaWikiSourceBadge();
        } else if (comment.is_from_filozofia_wiki) {
            badge = renderFilozofiaWikiSourceBadge();
        } else {
            badge = renderFandomSourceBadge();
        }
        container.appendChild(badge);
        return container;
    }
    return whenAdded(comment);
}

export function renderComment(comment: Comment, currentUserVote: Votetype | null, onVoteClicked: (
    comment: Comment, voteType: Votetype, upvote_element: HTMLElement, downvote_element: HTMLElement,
) => void, options?: { showSubjectBadge?: boolean; dimension?: "zajecia" | "egzamin" }): HTMLElement {

    const showSubjectBadge = options?.showSubjectBadge ?? true;
    // Gdy karta renderuje się na konkretnej zakładce (zajęcia/egzamin - patrz DimensionTab w
    // post_detail.ts), pokazujemy TYLKO ikony tej zakładki, nawet jeśli ta sama opinia ocenia
    // OBIE - inaczej na zakładce "Egzamin" potrafiły się pokazać razem gwiazdki (zajęcia) i ogień
    // (egzamin) na jednej karcie, co myliło (zgłoszenie: "przy egzaminie nie dawaj gwiazdek,
    // czasem są i gwiazdki i płomienie"). Bez options.dimension (np. inne miejsca wywołania)
    // pokazujemy oba wymiary jak dotychczas.
    const dimension = options?.dimension;

    const div = document.createElement("div");
    div.className = "comment";
    div.dataset.id = String(comment.id);

    const stars = (comment.rating !== null && dimension !== "egzamin") ? showStars(comment.rating) : null;

    const cornerElement = renderCommentCorner(comment);

    // Gwiazdki + "poleca/odradza" mają być NIERUCHOMĄ parą - zawsze w jednej linii, nawet na
    // wąskim telefonie. Osobna grupa (nie .comment-meta - ta może się zawinąć niżej razem z
    // przedmiotem) z flex-wrap:nowrap (patrz CSS), żeby na wąskim ekranie to WYŁĄCZNIE odznaka
    // przedmiotu (dłuższa i bardziej zmienna długość) schodziła do drugiej linii, nigdy ta para.
    const primaryGroup = document.createElement("div");
    primaryGroup.className = "comment-header-primary";
    if (stars) primaryGroup.appendChild(stars);
    if (dimension !== "egzamin") {
        const wouldTakeAgainBadge = renderCommentWouldTakeAgainBadge(comment);
        if (wouldTakeAgainBadge) primaryGroup.appendChild(wouldTakeAgainBadge);
        // RMP-style: sama liczba, nie drugi zestaw gwiazdek/ikon obok już istniejących (patrz
        // renderEaseOfPassingText w generics.ts).
        if (comment.class_difficulty !== null && comment.class_difficulty !== undefined) {
            const easeText = renderEaseOfPassingText(comment.class_difficulty);
            if (easeText) primaryGroup.appendChild(easeText);
        }
    }

    // Druga, opcjonalna ocena - egzamin (patrz Comment.exam_rating wyżej) - dokładana OBOK
    // gwiazdek zajęć, gdy obecna i gdy nie jesteśmy na zakładce "zajęcia" (patrz dimension wyżej).
    // Ikona i odznaka "poleca" są CELOWO w dwóch osobnych warunkach (nie jednym) - część opinii ma
    // exam_would_take_again bez exam_rating albo odwrotnie; zagnieżdżenie odznaki pod warunkiem
    // oceny chowało ją całkowicie w tym przypadku (zgłoszenie: opinie o egzaminie "znikały" mimo
    // ustawionego exam_would_take_again).
    if (dimension !== "zajecia" && comment.exam_rating !== null && comment.exam_rating !== undefined) {
        const examRatingEl = showStars(comment.exam_rating);
        examRatingEl.title = `Ocena egzaminu: ${EXAM_RATING_LABELS[comment.exam_rating] ?? comment.exam_rating}`;
        primaryGroup.appendChild(examRatingEl);
    }
    if (dimension !== "zajecia") {
        const examWouldTakeAgainBadge = renderExamWouldTakeAgainBadge(comment);
        if (examWouldTakeAgainBadge) primaryGroup.appendChild(examWouldTakeAgainBadge);
        if (comment.exam_difficulty !== null && comment.exam_difficulty !== undefined) {
            const easeText = renderEaseOfPassingText(comment.exam_difficulty);
            if (easeText) primaryGroup.appendChild(easeText);
        }
    }

    const meta = document.createElement("div");
    meta.className = "comment-meta";
    if (showSubjectBadge && comment.subject) {
        const subjectBadge = document.createElement("span");
        subjectBadge.className = "comment-subject-badge";
        subjectBadge.textContent = comment.subject.name;
        meta.appendChild(subjectBadge);
    }

    const body = document.createElement("p");
    body.style.whiteSpace = "pre-line";
    // Na zakładce (WPiA) każda część opinii osobno, bez "pod sobą" (na wyraźną prośbę 2026-10-15):
    // "Zajęcia" - tylko treść o zajęciach; "Egzamin" - tylko treść o egzaminie (exam_body), bez
    // etykiety "Egzamin:", bo zakładka już to mówi. Wyjątek: opinie z importu wpia-wiki i opinie
    // tylko o egzaminie trzymają całą treść w `body` (patrz notka przy Comment.exam_body) - na
    // zakładce "Egzamin" pokazujemy wtedy `body`. Opinia o obu wymiarach bez osobnej treści o
    // egzaminie pokazuje tam same oceny, bez tekstu o zajęciach.
    if (dimension === "egzamin") {
        body.textContent = comment.exam_body
            || ((comment.is_from_wpia_wiki || comment.rating === null) ? comment.body : "");
    } else {
        body.textContent = comment.body;
    }

    // Osobny akapit dla treści o egzaminie (Comment.exam_body) - tylko gdy faktycznie wypełniony
    // i karta NIE jest na konkretnej zakładce (patrz wyżej) - z krótką etykietą, żeby było jasne,
    // że to komentarz o EGZAMINIE, nie kontynuacja opinii o zajęciach wyżej.
    let examBodyEl: HTMLElement | null = null;
    if (comment.exam_body && !dimension) {
        examBodyEl = document.createElement("p");
        examBodyEl.className = "comment-exam-body";
        const examBodyLabel = document.createElement("strong");
        examBodyLabel.textContent = "Egzamin: ";
        examBodyEl.appendChild(examBodyLabel);
        examBodyEl.appendChild(document.createTextNode(comment.exam_body));
        examBodyEl.style.whiteSpace = "pre-line";
    }

    const upvotes = document.createElement("button");
    upvotes.type = "button";
    upvotes.className = "upvote-btn";
    upvotes.setAttribute("aria-label", "Głosuj na tak");
    upvotes.innerHTML = `${UPVOTE_ICON_SVG} (<span class="upvote-count">${comment.upvotes}</span>)`;

    const downvotes = document.createElement("button");
    downvotes.type = "button";
    downvotes.className = "downvote-btn";
    downvotes.setAttribute("aria-label", "Głosuj na nie");
    downvotes.innerHTML = `${DOWNVOTE_ICON_SVG} (<span class="downvote-count">${comment.downvotes}</span>)`;

    upvotes.setAttribute("aria-pressed", String(currentUserVote == "upvote"));
    downvotes.setAttribute("aria-pressed", String(currentUserVote == "downvote"));

    if (currentUserVote == "upvote"){
        upvotes.classList.add("active-vote");
    }
    else if (currentUserVote == "downvote"){
        downvotes.classList.add("active-vote");
    }

    // id ujemne = jeszcze niepotwierdzony, optymistyczny wpis (patrz initCommentForm/optimistic
    // UI) - nie odpowiada żadnemu prawdziwemu komentarzowi w bazie jeszcze, więc głosowanie
    // (i zgłaszanie) na nim nie miałoby czego zaktualizować. Znika, gdy serwer potwierdzi
    // publikację i podmieni ten wpis na prawdziwy (patrz onCommentConfirmed w post_detail.ts).
    const isOptimistic = comment.id < 0;
    if (isOptimistic) {
        upvotes.disabled = true;
        downvotes.disabled = true;
    }

    upvotes.addEventListener("click", () => {
            onVoteClicked(comment, "upvote", upvotes, downvotes);
        });

    downvotes.addEventListener("click", () => {
            onVoteClicked(comment, "downvote", upvotes, downvotes);
        });

    // Zgłoszenie nie usuwa komentarza - tylko oznacza go do sprawdzenia przez moderatora
    // (patrz Comment.VISIBLE_STATUSES w backendzie), więc pozostaje widoczny dla wszystkich.
    // Schowane pod menu "⋮" w prawym dolnym rogu komentarza.
    const menu = document.createElement("div");
    menu.className = "comment-menu";

    const menuToggle = document.createElement("button");
    menuToggle.type = "button";
    menuToggle.className = "comment-menu-toggle";
    menuToggle.innerHTML = "&#8942;"; // ⋮
    menuToggle.setAttribute("aria-label", "Więcej opcji");
    menuToggle.setAttribute("aria-haspopup", "true");
    menuToggle.setAttribute("aria-expanded", "false");

    const menuDropdown = document.createElement("div");
    menuDropdown.className = "comment-menu-dropdown";

    const reportBtn = document.createElement("button");
    reportBtn.type = "button";
    reportBtn.className = "report-btn";

    // Formularz zgłoszenia (powód + wyjaśnienie + opcjonalny kontakt) - wymagane formalnie
    // przez zgłoszenie nielegalnej treści w rozumieniu art. 16 ust. 2 DSA (patrz
    // CommentReportView w backendzie); samo oznaczenie bez uzasadnienia nie wystarczało.
    // Chowany, dopóki ktoś nie kliknie "Zgłoś".
    const reportForm = document.createElement("form");
    reportForm.className = "report-form";
    reportForm.hidden = true;

    const reasonSelect = document.createElement("select");
    reasonSelect.className = "styled-select";
    reasonSelect.required = true;
    for (const { value, label } of REPORT_REASONS) {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = label;
        reasonSelect.appendChild(option);
    }

    const explanationInput = document.createElement("textarea");
    explanationInput.rows = 3;
    explanationInput.required = true;
    explanationInput.placeholder = "Krótko wyjaśnij, na czym polega naruszenie...";

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.placeholder = "Imię i nazwisko (opcjonalnie)";

    const emailInput = document.createElement("input");
    emailInput.type = "email";
    emailInput.placeholder = "Twój e-mail (opcjonalnie)";

    // Wymagane oświadczenie o dobrej wierze (art. 16 ust. 2 lit. d DSA) - w przeciwieństwie do
    // imienia/e-maila wyżej NIE jest opcjonalne, patrz walidacja w submit handlerze niżej i w
    // CommentReportSerializer.validate_good_faith_confirmed w backendzie (druga linia obrony,
    // gdyby ktoś ominął ten formularz i uderzył bezpośrednio w API).
    const goodFaithLabel = document.createElement("label");
    goodFaithLabel.className = "report-form-checkbox-label";
    const goodFaithCheckbox = document.createElement("input");
    goodFaithCheckbox.type = "checkbox";
    goodFaithCheckbox.required = true;
    goodFaithLabel.append(
        goodFaithCheckbox,
        " Oświadczam, w dobrej wierze, że podane przeze mnie informacje są prawdziwe i kompletne."
    );

    const reportError = document.createElement("span");
    reportError.className = "error";

    const submitReportBtn = document.createElement("button");
    submitReportBtn.type = "submit";
    submitReportBtn.textContent = "Wyślij zgłoszenie";

    const cancelReportBtn = document.createElement("button");
    cancelReportBtn.type = "button";
    cancelReportBtn.className = "report-form-cancel";
    cancelReportBtn.textContent = "Anuluj";

    const reportFormActions = document.createElement("div");
    reportFormActions.className = "report-form-actions";
    reportFormActions.append(submitReportBtn, cancelReportBtn);

    reportForm.append(
        reasonSelect, explanationInput, nameInput, emailInput, goodFaithLabel,
        reportError, reportFormActions
    );

    // Widoczne potwierdzenie po udanym wysłaniu - poprzednio kod od razu wołał closeMenu()
    // (patrz submit handler niżej), więc CAŁE menu znikało w tej samej chwili, w której
    // reportBtn zmieniał się na "Zgłoszono" - użytkownik nie miał jak tego zobaczyć, bo przycisk
    // był już schowany razem z zamkniętym menu. Ten komunikat zostaje widoczny przez chwilę
    // PRZED zamknięciem, żeby wysłanie było namacalnie potwierdzone, nie tylko "ciche".
    const reportSuccessMessage = document.createElement("p");
    reportSuccessMessage.className = "report-success-message";
    reportSuccessMessage.hidden = true;
    reportSuccessMessage.textContent = "✓ Dziękujemy, zgłoszenie zostało wysłane.";

    const markAsReported = () => {
        reportBtn.disabled = true;
        reportBtn.textContent = "Zgłoszono";
        reportBtn.classList.add("reported");
        reportBtn.style.display = "";
        reportForm.hidden = true;
    };

    if (comment.status === "reported") {
        markAsReported();
    } else {
        reportBtn.textContent = "Zgłoś";
    }

    reportBtn.addEventListener("click", (event) => {
        // Zapobiega ewentualnemu przypadkowemu zamknięciu menu przez handleOutsideClick niżej
        // (ten klik i tak jest w obrębie `menu`, więc formalnie nie powinno do tego dojść - ale
        // to tania, defensywna gwarancja, że "Zgłoś" zawsze otwiera formularz, niezależnie od
        // kolejności odpalania nasłuchiwaczy na `document`).
        event.stopPropagation();
        reportBtn.style.display = "none";
        reportForm.hidden = false;
        explanationInput.focus();
    });

    cancelReportBtn.addEventListener("click", (event) => {
        event.stopPropagation();
        reportForm.hidden = true;
        reportBtn.style.display = "";
    });

    let menuOpen = false;
    function closeMenu(): void {
        if (!menuOpen) return;
        menuOpen = false;
        menuDropdown.classList.remove("open");
        menuToggle.setAttribute("aria-expanded", "false");
        document.removeEventListener("click", handleOutsideClick);
    }
    function handleOutsideClick(event: MouseEvent): void {
        if (!menu.contains(event.target as Node)) {
            closeMenu();
        }
    }
    menuToggle.addEventListener("click", (event) => {
        event.stopPropagation();
        if (menuOpen) {
            closeMenu();
            return;
        }
        menuOpen = true;
        menuDropdown.classList.add("open");
        menuToggle.setAttribute("aria-expanded", "true");
        document.addEventListener("click", handleOutsideClick);
    });

    reportForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        event.stopPropagation();
        reportError.textContent = "";

        const explanation = explanationInput.value.trim();
        if (explanation.length < 5) {
            reportError.textContent = "Wyjaśnienie musi mieć co najmniej 5 znaków.";
            return;
        }
        if (!goodFaithCheckbox.checked) {
            reportError.textContent = "Musisz potwierdzić, że zgłoszenie jest złożone w dobrej wierze.";
            return;
        }

        // Wysyłka maila do administratora dzieje się synchronicznie w tym samym żądaniu (patrz
        // CommentReportView.post w backendzie) - prawdziwe połączenie SMTP potrafi zająć kilka
        // sekund, więc samo disabled bez zmiany tekstu wyglądało na zawieszenie/nic się nie
        // dzieje. Tekst wraca do "Wyślij zgłoszenie" tylko w gałęzi błędu niżej - w gałęzi
        // sukcesu przycisk i tak zaraz znika (patrz reportForm.hidden = true).
        submitReportBtn.disabled = true;
        submitReportBtn.textContent = "Wysyłanie...";
        const result = await api.reportComment(comment.id, {
            reason: reasonSelect.value,
            explanation,
            reporter_name: nameInput.value.trim(),
            email: emailInput.value.trim(),
            good_faith_confirmed: goodFaithCheckbox.checked,
        });
        if (result.ok) {
            reportForm.hidden = true;
            reportSuccessMessage.hidden = false;
            // Zostaje widoczne przez chwilę - dopiero potem zwijamy formularz do stanu
            // "Zgłoszono" i zamykamy menu, żeby potwierdzenie faktycznie zdążyło się zobaczyć.
            window.setTimeout(() => {
                markAsReported();
                reportSuccessMessage.hidden = true;
                closeMenu();
            }, 1800);
        } else {
            // Pokazujemy PRAWDZIWY powód błędu (np. limit czasowy "poczekaj chwilę", błąd
            // sieci) zamiast jednego ogólnego tekstu - wcześniej `reportComment` zwracał samo
            // boolean, więc każda przyczyna (w tym np. zablokowanie żądania przez rozszerzenie
            // przeglądarki blokujące adresy zawierające "report") wyglądała identycznie jak
            // "spróbuj ponownie", bez śladu w konsoli ułatwiającego diagnozę.
            console.error("Zgłoszenie komentarza nie powiodło się:", result.error);
            reportError.textContent = `Nie udało się wysłać zgłoszenia: ${result.error}`;
            submitReportBtn.disabled = false;
            submitReportBtn.textContent = "Wyślij zgłoszenie";
        }
    });

    menuDropdown.append(reportBtn, reportForm, reportSuccessMessage);
    menu.append(menuToggle, menuDropdown);

    const headerRow = document.createElement("div");
    headerRow.className = "comment-header-row";
    headerRow.append(primaryGroup, meta);

    div.appendChild(headerRow);
    if (body.textContent) div.appendChild(body);
    if (examBodyEl) div.appendChild(examBodyEl);
    div.appendChild(upvotes);
    div.appendChild(downvotes);
    // Zgłoszenie (patrz "menu"/isOptimistic wyżej) tak samo bez sensu na jeszcze niepotwierdzonym
    // wpisie jak głosowanie - nie ma czego zgłaszać, dopóki serwer nie potwierdzi publikacji.
    if (!isOptimistic) div.appendChild(menu);
    // Data (lub odznaka Fandom) w prawym górnym rogu - celowo dyskretna, nie ma się "rzucać
    // w oczy" jako pierwsza.
    div.appendChild(cornerElement);

    return div;
}



export interface CommentFormOptions {
    subjects: Subject[];
    // undefined = pokaż wyszukiwarkę przedmiotu (formularz główny);
    // null = przedmiot na stałe ustawiony na "Ogólne" (sekcja Ogólne, przycisk +);
    // Subject = przedmiot na stałe ustawiony na dany przedmiot (przycisk + przy nagłówku).
    fixedSubject?: Subject | null;
    // Odpala się przy KAŻDYM udanym zapisie komentarza (aktywnym i czekającym na moderację) - w
    // odróżnieniu od onCommentAdded (initCommentForm niżej), które odpala się tylko dla aktywnego
    // (widocznego) komentarza. Limit "jedna opinia na profil w ciągu 15 min" (patrz
    // pages/api/comments.py) egzekwowany jest niezależnie od statusu - hasAlreadyReviewedThisPost
    // w post_detail.ts musi więc wiedzieć o KAŻDYM zapisie, nie tylko o aktywnych, inaczej
    // podpowiedź limitu (i wygląd przycisku "Dodaj opinię") nigdy by się nie uruchomiły dla
    // komentarzy, które trafiły do moderacji. Dostaje zapisany komentarz (ze statusem) - patrz
    // quickReview.ts, gdzie to jedyny sposób odróżnienia "aktywny"/"czeka na moderację" w
    // momencie, gdy trzeba natychmiast przekierować (przed doczekaniem feedback/onAutoClose niżej).
    onSubmitted?: (comment: Comment) => void;
    // Odpala się razem z automatycznym schowaniem formularza po udanym wysłaniu (patrz
    // scheduleFullHide/setTimeout niżej w initCommentForm) - używane przez post_detail.ts do
    // zamknięcia modala (patrz ADD_REVIEW_MODAL_ENABLED w generics.ts), gdy formularz jest w nim
    // osadzony. Poza tym trybem po prostu nie jest przekazywane (formularz i tak chowa się sam
    // przez collapsible-collapsed/comment-form-collapsed).
    onAutoClose?: () => void;
    // Odpala się NATYCHMIAST po udanym wysłaniu, zamiast zwykłego 8s odczekania na feedback
    // wewnątrz formularza (patrz "brak notifyPrompts" niżej w initCommentForm) - TYLKO gdy
    // naprawdę nie ma nic więcej do zrobienia w tym formularzu (komentarz aktywny od razu, bez
    // nowo dodanego przedmiotu/wydziału czekającego na moderację, patrz notifyTargets niżej -
    // inaczej trzeba zostawić czas na widżet "zostaw e-mail"). Używane przez dialog "Dodaj
    // opinię" na profilu (post_detail.ts), żeby zamknąć okienko OD RAZU i pokazać zamiast niego
    // baner na stronie (zgłoszenie: "biały popup co trzeba zamknąć" zamiast dawnego zielonego
    // banera). Gdy podane, przejmuje CAŁĄ odpowiedzialność za zamknięcie/schowanie formularza -
    // initCommentForm nie planuje wtedy własnego 8s timera ani nie woła onAutoClose.
    onInstantSuccess?: (comment: Comment) => void;
    // Pełny tryb optymistyczny okienka (na wyraźną prośbę 2026-10-15): onOptimisticClose zamyka
    // okienko OD RAZU po kliknięciu "wyślij" (opinia jest już na liście, patrz onCommentAdded), bez
    // czekania na moderację AI. Gdy po odpowiedzi serwera jest coś do pokazania (podpowiedź AI,
    // czekanie na moderację, widżet e-mail, błąd), onReopen otwiera okienko z powrotem - z tym
    // samym formularzem i wpisaną treścią. Pomijane przy "Wyślij bez zmian" (okienko i tak jest
    // wtedy otwarte i ma pokazać komunikat o moderacji).
    onOptimisticClose?: () => void;
    onReopen?: () => void;
    // Optimistic UI (na wyraźną prośbę 2026-09-05: "komentarz publikowany od razu, jeśli coś nie
    // tak to wtedy się pokazuje") - onCommentAdded (3. argument initCommentForm) dostaje komentarz
    // OPTYMISTYCZNIE, z tymczasowym ujemnym id, ZANIM odpowie serwer. Gdy serwer faktycznie
    // potwierdzi aktywną publikację, onCommentConfirmed podmienia ten tymczasowy wpis na prawdziwy
    // (prawdziwe id, potrzebne np. do głosowania) - dopiero WTEDY nalicza się średnie/liczniki
    // (patrz confirmComment w post_detail.ts), żeby nie policzyć tej samej opinii dwa razy. Gdy
    // serwer zamiast tego wstrzyma komentarz do moderacji (albo zapis się nie powiedzie),
    // onCommentRemoved cofa optymistyczny wpis z listy - w obu przypadkach komentarz i tak
    // NIGDY nie był naprawdę publiczny, więc nic nie trzeba było naliczać ani odliczać.
    onCommentConfirmed?: (tempId: number, realComment: Comment) => void;
    onCommentRemoved?: (tempId: number) => void;
    // Picker wydziału (patrz renderFacultyPicker) - undefined = nie pokazuj go wcale (typowy
    // przypadek: osoba ma już jednoznaczny, potwierdzony wydział). Niepusta tablica (może być
    // pusta - patrz PLACEHOLDER_FACULTY_SLUG w views.post_detail) = pokaż, z tymi kandydatami
    // podpowiedzianymi na starcie (dropuje się do nich pełna lista wydziałów uczelni w tle).
    facultyCandidates?: FacultyCandidate[];
    universitySlug?: string;
    // Slug wydziału, z listy którego trafiono na ten profil (patrz ?wydzial= w post_detail.ts) -
    // jeśli jest wśród facultyCandidates, picker zaznacza go od razu.
    preselectFacultySlug?: string | null;
    // Wydział już ustalony PRZEZ WOŁAJĄCEGO (patrz renderAddPersonForm w addLecturer.ts - tam
    // wydział wybiera się we własnym, osobnym polu formularza - wyszarzonym albo z wyszukiwarką -
    // zanim w ogóle dojdzie do tego formularza komentarza) - wysyłany jako resolved_faculty_id
    // razem z komentarzem, BEZ pokazywania drugiego, redundantnego pickera wydziału tutaj (ten
    // niżej pokazuje się tylko, gdy facultyCandidates !== undefined - dla tego wołającego zawsze
    // undefined).
    fixedResolvedFacultyId?: number;
    // Pokazuje dodatkową, w pełni opcjonalną sekcję "Egzamin: Trudność" (+ opcjonalne "Wybrałbyś
    // ponownie?" dla egzaminu) obok istniejącej oceny zajęć - patrz supports_exam_dimensions w
    // views.post_detail. W odróżnieniu od oceny zajęć (rating/would_take_again wyżej, zawsze
    // wymagane bez zmian) ta sekcja NIE jest wymagana w żadnym stopniu - brak interakcji z nią
    // po prostu wysyła exam_difficulty/exam_would_take_again jako null, tak jak dotychczas.
    supportsExamDimensions?: boolean;
}

// Owija podane elementy tak, żeby dało się je płynnie schować/pokazać bez przeskoku layoutu -
// patrz .collapsible/.collapsible-inner w style.css (sztuczka z grid-template-rows: 1fr -> 0fr).
// Chowanie/pokazywanie: outer.classList.toggle("collapsible-collapsed").
export function makeCollapsible(...children: HTMLElement[]): { outer: HTMLDivElement, inner: HTMLDivElement } {
    const outer = document.createElement("div");
    outer.className = "collapsible";
    const inner = document.createElement("div");
    inner.className = "collapsible-inner";
    inner.append(...children);
    outer.appendChild(inner);
    return { outer, inner };
}

// Nowa wersja - ona powiadamia aplikację, że powinna wyświetlić nowy komentarz
export function initCommentForm(
    postId: number,
    container: HTMLElement,
    onCommentAdded: (newComment: Comment) => void,
    options: CommentFormOptions
): { setExamPrimary: (examPrimary: boolean) => void } {
    container.replaceChildren();

    let selectedRating = 0;
    const showSubjectPicker = options.fixedSubject === undefined;
    const form = document.createElement("form");

    const starRatingElement = renderStarRating(
        rating => {
            selectedRating = rating;
        }
    );

    // Łatwość zaliczenia zajęć/egzaminu (patrz Comment.class_difficulty/exam_difficulty w
    // models.py - nazwa pola w bazie zostaje, zmienia się etykieta/kierunek skali,
    // renderEaseOfPassingText w generics.ts dla wyświetlania) - na razie tylko tam, gdzie w ogóle
    // istnieje podział zajęcia/egzamin (options.supportsExamDimensions, na razie WPiA UW) - na
    // pozostałych wydziałach "ćwiczenia" nie są odrębnym pojęciem od zwykłej oceny zajęć.
    // Opcjonalne (0 -> null), tak samo jak exam_rating - nie każda opinia musi oceniać łatwość.
    // WŁASNY zestaw ikony/etykiet (nie DIFFICULTY_LABELS/🔥 z subjects.ts, tam dla Subject.difficulty
    // - INNY kierunek skali: tu 5 = bardzo łatwo zaliczyć = dobrze, jak gwiazdki; 🔥 zasugerowałoby
    // "5 = najtrudniej", więc inna ikona, żeby dwóch odwrotnych skal nie pomylić wizualnie).
    const EASE_OF_PASSING_ICON = "\u{1F393}"; // 🎓
    const EASE_OF_PASSING_LABELS: { [key: number]: string } = {
        1: "Bardzo trudne",
        2: "Trudne",
        3: "Średnie",
        4: "Łatwe",
        5: "Bardzo łatwe",
    };
    // EASE_OF_PASSING_ENABLED (generics.ts) - schowane CAŁOŚCIOWO na wyraźną prośbę: dopóki
    // false, ten picker w ogóle nie powstaje (użytkownik nie może niczego wpisać), niezależnie od
    // supportsExamDimensions - ten sam wzorzec co WOULD_TAKE_AGAIN_ENABLED przy
    // wouldTakeAgainField wyżej.
    let selectedClassDifficulty = 0;
    const classDifficultyWidget = (options.supportsExamDimensions && EASE_OF_PASSING_ENABLED) ? renderDifficultyRating(value => {
        selectedClassDifficulty = value;
    }, EASE_OF_PASSING_ICON, EASE_OF_PASSING_LABELS, "Łatwość zaliczenia zajęć") : null;
    let selectedExamDifficulty = 0;
    const examDifficultyWidget = (options.supportsExamDimensions && EASE_OF_PASSING_ENABLED) ? renderDifficultyRating(value => {
        selectedExamDifficulty = value;
    }, EASE_OF_PASSING_ICON, EASE_OF_PASSING_LABELS, "Łatwość zaliczenia egzaminu") : null;

    const ratingError = document.createElement("span");
    ratingError.className = "error";

    let subjectPicker: ReturnType<typeof renderSubjectPicker> | null = null;
    const subjectError = document.createElement("span");
    subjectError.className = "error";

    // Picker wydziału - patrz CommentFormOptions.facultyCandidates. Pokazuje się tylko, gdy osoba
    // wciąż siedzi w technicznym "Bez przypisanego wydziału" (patrz needs_faculty_confirmation w
    // views.post_detail) - w tej sytuacji pole jest WYMAGANE (patrz walidacja w submit handlerze
    // niżej i facultyError), tak samo jak przedmiot.
    const facultyPicker = options.facultyCandidates !== undefined && options.universitySlug
        ? renderFacultyPicker(options.universitySlug, options.facultyCandidates, options.preselectFacultySlug ?? null)
        : null;
    const facultyError = document.createElement("span");
    facultyError.className = "error";

    // Funkcjonalność jest na razie schowana (patrz WOULD_TAKE_AGAIN_ENABLED w generics.ts) -
    // gdy wyłączona, pole w ogóle nie powstaje/nie renderuje się, walidacja go nie wymaga,
    // a do API leci "would_take_again: null" (patrz payload niżej), tak jak dla starych
    // komentarzy sprzed dodania tego pola.
    const wouldTakeAgainField = WOULD_TAKE_AGAIN_ENABLED ? renderWouldTakeAgainField() : null;
    const wouldTakeAgainError = document.createElement("span");
    wouldTakeAgainError.className = "error";

    // Sekcja "Egzamin" - patrz CommentFormOptions.supportsExamDimensions. Opcjonalna co do
    // WALIDACJI (submit zawsze wymaga oceny zajęć - patrz validateComment niżej, bez zmian) -
    // to, co faktycznie zależy od tego, na której zakładce (Zajęcia/Egzamin) stał user, gdy
    // otworzył formularz, to WYŁĄCZNIE które z dwóch "klastrów" pól (zajęcia/egzamin) jest
    // widoczne od razu ("primary"), a które chowa się za przyciskiem "Oceń od razu X" - patrz
    // setExamPrimaryFn/zwracany handle na końcu funkcji i setExamPrimary w post_detail.ts.
    let selectedExamRating = 0;
    const examRatingWidget = options.supportsExamDimensions ? renderStarRating(value => {
        selectedExamRating = value;
    }, EXAM_RATING_LABELS) : null;
    const examWouldTakeAgainField = options.supportsExamDimensions ? renderWouldTakeAgainField("Czy poleciłbyś/poleciłabyś ten egzamin ponownie?") : null;

    // Osobne pole tekstowe dla egzaminu (Comment.exam_body) - "Twoja opinia" niżej dotyczy
    // zajęć, to pole wyłącznie egzaminu (na wyraźną prośbę - dwa niezależne pola tekstowe,
    // tak jak dwa niezależne zestawy ocen).
    let examBodyTextarea: HTMLTextAreaElement | null = null;
    if (options.supportsExamDimensions) {
        examBodyTextarea = document.createElement("textarea");
        examBodyTextarea.placeholder = "Co warto wiedzieć o samym egzaminie?";
        examBodyTextarea.rows = 2;
        trackUnsavedInput(examBodyTextarea);
    }

    // Przycisk wysyłki - JEDEN w całym formularzu. Gdy supportsExamDimensions, ląduje w parze
    // z przełącznikiem "Oceń od razu X" (ciemny, bez własnej klasy - domyślny styl
    // .comment-form-box button jest już "ciemny") jako "Dodaj opinię" (jasny,
    // .comment-form-btn-light) - patrz buttonsRow niżej. W przeciwnym razie zostaje w bottomRow
    // na samym końcu, jak dotychczas (patrz "Podziel się opinią" niżej).
    // 2026-10-15 (na wyraźną prośbę): kolory zamienione - wysyłka jest teraz domyślna (ciemna),
    // a przełącznik "Oceń też egzamin/zajęcia" jasny (.comment-form-btn-light).
    const submitBtn = document.createElement("button");
    submitBtn.type = "submit";
    if (options.supportsExamDimensions) {
        submitBtn.textContent = "Dodaj opinię";
    }

    const ratingFieldWrap = document.createElement("div");
    ratingFieldWrap.className = "rating-field";
    // Jawna etykieta "Ocena:" obok gwiazdek - same gwiazdki (bez podpisu, bez oczywistego stanu
    // "aktywne") nie sygnalizowały, że w ogóle trzeba w nie kliknąć (zgłoszenie: user nie wiedział,
    // że gwiazdki są klikalne).
    const ratingLabel = document.createElement("span");
    ratingLabel.className = "field-label-text";
    ratingLabel.textContent = "Ocena:";
    ratingFieldWrap.append(ratingLabel, starRatingElement.element);
    if (classDifficultyWidget) {
        const classDifficultyLabel = document.createElement("span");
        classDifficultyLabel.className = "field-label-text";
        classDifficultyLabel.textContent = "Łatwość zaliczenia:";
        ratingFieldWrap.append(classDifficultyLabel, classDifficultyWidget.element);
    }

    // Jawna etykieta NAD polem - sam placeholder (poprzednio jedyny opis pola) znika, gdy user
    // zacznie pisać, więc przestaje tłumaczyć, o co chodzi w tym polu (zgłoszenie: "komentarz
    // ogólny jest niejasny"). Placeholder zostaje jako dodatkowa podpowiedź TREŚCI, etykieta
    // odpowiada za samo "co to za pole".
    const bodyLabel = document.createElement("span");
    bodyLabel.className = "field-label-text comment-body-label";
    bodyLabel.textContent = "Twoja opinia:";

    const bodyTextarea = document.createElement("textarea");
    bodyTextarea.placeholder = "Co inni studenci powinni wiedzieć o tym prowadzącym?";
    bodyTextarea.required    = true;
    bodyTextarea.rows        = 3;
    trackUnsavedInput(bodyTextarea);
    // const vine = attachVine(bodyTextarea);

    const bodyError = document.createElement("span");
    bodyError.className = "error";

    // Etykieta+pole+błąd RAZEM w jednym kontenerze - tak samo jak .rating-field/.subject-picker
    // wyżej - żeby w modalu (patrz .add-review-dialog .collapsible-inner w CSS, flex + gap
    // między POLAMI) liczyły się jako JEDEN element. Bez tego bodyLabel i bodyTextarea były
    // osobnymi, bezpośrednimi dziećmi collapsible-inner - dostawały więc odstęp z gap MIĘDZY SOBĄ
    // (myślany na odstęp między różnymi polami), do którego dokładał się jeszcze własny
    // margin-bottom etykiety - stąd nienaturalnie wielka przerwa akurat pod "Twoja opinia".
    const bodyFieldWrap = document.createElement("div");
    bodyFieldWrap.className = "comment-body-field";
    bodyFieldWrap.append(bodyLabel, bodyTextarea, bodyError);

    // Gwiazdki/trudność, przedmiot - w tej kolejności, w jednej linii. Miejsce na przedmiot
    // (subjectSlot) wędruje między zajeciaRow/examRow razem z tym, który klaster jest akurat
    // "primary" - patrz setExamPrimaryFn niżej. W formularzu "+" przedmiot jest już ustalony,
    // więc zamiast wyszukiwarki pokazujemy samą (kompaktową) odznakę z jego nazwą.
    const zajeciaRow = document.createElement("div");
    zajeciaRow.className = "comment-form-row";
    zajeciaRow.appendChild(ratingFieldWrap);

    let subjectSlot: HTMLElement;
    if (showSubjectPicker) {
        subjectPicker = renderSubjectPicker(postId, options.subjects);
        subjectSlot = subjectPicker.element;
    } else {
        const contextBadge = document.createElement("div");
        contextBadge.className = "comment-form-context-field comment-subject-badge";
        contextBadge.textContent = options.fixedSubject ? options.fixedSubject.name : GENERAL_SUBJECT_LABEL;
        subjectSlot = contextBadge;
    }

    // "Podziel się opinią" zamiast biurokratycznego "Opublikuj" - to samo ramowanie co
    // placeholder wyżej, ale krótkie i bez przesady (celowo NIE "Pomóż komuś dziś!" ani
    // nic równie na siłę). Tylko gdy NIE ma pary z przełącznikiem egzaminu (patrz submitBtn
    // wyżej) - inaczej ten tekst/przycisk już istnieje w buttonsRow.
    if (!options.supportsExamDimensions) {
        submitBtn.textContent = "Podziel się opinią";
    }

    // Przycisk wysyłki w osobnym, zawsze-ostatnim wierszu - TYLKO gdy nie jest już sparowany
    // z przełącznikiem egzaminu (patrz buttonsRow niżej, ten sam submitBtn ląduje tam zamiast
    // tutaj).
    const bottomRow = document.createElement("div");
    bottomRow.className = "comment-form-bottom-row";

    let primaryClusterHost: HTMLElement | null = null;
    let dualClusterWrap: HTMLElement | null = null;
    let setExamPrimaryFn: ((examPrimary: boolean) => void) | null = null;
    let revealZajeciaIfHidden: (() => void) | null = null;
    let resetSecondaryOpenState: (() => void) | null = null;

    if (examRatingWidget && examWouldTakeAgainField && examBodyTextarea) {
        // bodyTextarea.required=true (ustawione wyżej) blokuje wysyłkę na poziomie przeglądarki -
        // natywna walidacja Chromium nie uznaje "required, ale w rodzicu z display:none" za
        // zwolnione z walidacji (mimo że pole faktycznie jest niewidoczne), więc submit cichnie z
        // "An invalid form control ... is not focusable" w konsoli, gdy zajęcia są sekcją
        // schowaną (patrz setExamPrimaryFn niżej - bodyTextarea trafia wtedy do
        // secondaryClusterFields). Wyłączone tu na stałe dla formularzy z dwoma klastrami - i tak
        // mamy własną walidację (patrz validateComment w submit handlerze) z tym samym błędem, ale
        // widocznym w błędzie pod polem, nie jako niema, natywna blokada.
        bodyTextarea.required = false;

        const examRatingFieldWrap = document.createElement("div");
        examRatingFieldWrap.className = "rating-field";
        const examRatingLabel = document.createElement("span");
        examRatingLabel.className = "field-label-text";
        examRatingLabel.textContent = "Ocena:";
        examRatingFieldWrap.append(examRatingLabel, examRatingWidget.element);
        if (examDifficultyWidget) {
            const examDifficultyLabel = document.createElement("span");
            examDifficultyLabel.className = "field-label-text";
            examDifficultyLabel.textContent = "Łatwość zaliczenia:";
            examRatingFieldWrap.append(examDifficultyLabel, examDifficultyWidget.element);
        }

        const examRow = document.createElement("div");
        examRow.className = "comment-form-row";
        examRow.appendChild(examRatingFieldWrap);

        const examBodyFieldWrap = document.createElement("div");
        examBodyFieldWrap.className = "comment-body-field";
        examBodyFieldWrap.append(examBodyTextarea);

        // Dwa "klastry" pól - dokładnie te same węzły, które wcześniej zawsze stały u góry
        // (zajeciaRow/bodyFieldWrap/wouldTakeAgainField), plus ich egzaminowy odpowiednik - tu
        // tylko GRUPOWANE, żeby dało się je hurtowo przenosić między "widoczne od razu"
        // (primaryClusterHost) a "za przyciskiem" (secondaryClusterFields).
        const zajeciaCluster = document.createElement("div");
        zajeciaCluster.className = "comment-primary-cluster";
        zajeciaCluster.append(zajeciaRow, ratingError, bodyFieldWrap);
        if (wouldTakeAgainField) zajeciaCluster.append(wouldTakeAgainField.element, wouldTakeAgainError);

        const examCluster = document.createElement("div");
        examCluster.className = "comment-primary-cluster";
        examCluster.append(examRow, examBodyFieldWrap, examWouldTakeAgainField.element);

        primaryClusterHost = document.createElement("div");
        primaryClusterHost.className = "comment-primary-cluster-host";

        const toggleBtn = document.createElement("button");
        toggleBtn.type = "button";
        toggleBtn.className = "comment-form-btn-light";

        const buttonsRow = document.createElement("div");
        buttonsRow.className = "comment-exam-buttons-row";
        buttonsRow.append(toggleBtn, submitBtn);

        const secondaryClusterFields = document.createElement("div");
        secondaryClusterFields.className = "comment-exam-section-fields";
        // style.display (NIE atrybut hidden) - .comment-exam-section-fields ma jawne "display:
        // flex" w CSS, które (ta sama specyficzność co [hidden], późniejsza reguła wygrywa) biłoby
        // atrybut hidden dokładnie tak samo jak .add-review-btn wcześniej (patrz post_detail.ts/
        // updateDimensionView) - naprawione tutaj z góry, zamiast powtarzać ten sam błąd.
        secondaryClusterFields.style.display = "none";

        dualClusterWrap = document.createElement("div");
        dualClusterWrap.className = "comment-exam-section";
        dualClusterWrap.append(buttonsRow, secondaryClusterFields);

        // true (przeciwnie do domyślnego stanu "zajęcia primary") - wymusza, żeby setExamPrimaryFn(false)
        // niżej NAPRAWDĘ wykonał pierwszą budowę (przeniesienie subjectSlot, wypełnienie
        // primaryClusterHost itd.), zamiast zostać wyciszone przez własny early-return "nic się
        // nie zmieniło" (który patrzy właśnie na examIsPrimary).
        let examIsPrimary = true;
        let secondaryOpen = false;

        // Odsłania sekcję "za przyciskiem" - oba guziki, które dotąd stały razem (przełącznik +
        // wysyłka), znikają (na wyraźną prośbę - "niech te dwa guziki znikną"), submitBtn ląduje
        // na samym dole, PO odsłoniętych polach, więc zostaje widoczny/osiągalny, gdy user
        // dojdzie do końca. Płynne przewinięcie do świeżo odsłoniętych pól - inaczej (zwłaszcza w
        // modalu <dialog>, patrz ADD_REVIEW_MODAL_ENABLED w post_detail.ts) łatwo nie zauważyć, że
        // cokolwiek się w ogóle pojawiło niżej.
        function openSecondary(): void {
            if (secondaryOpen) return;
            secondaryOpen = true;
            secondaryClusterFields.style.display = "flex";
            buttonsRow.style.display = "none";
            // Jasny wariant (.comment-form-btn-light) miał sens obok ciemnego przełącznika
            // "Oceń od razu X" (patrz buttonsRow) - gdy oba znikają i submitBtn zostaje JEDYNYM
            // przyciskiem na dole formularza, jasny/obrysowany wygląd sprawiał, że wyglądał na
            // nieaktywny (zgłoszenie: "przycisk na końcu niech będzie już ciemny"). Domyślny,
            // ciemny styl .comment-form-box button wraca sam, gdy usuniemy tę jedną klasę.
            dualClusterWrap!.appendChild(submitBtn);
            requestAnimationFrame(() => {
                secondaryClusterFields.scrollIntoView({ behavior: "smooth", block: "nearest" });
            });
        }

        function closeSecondary(): void {
            secondaryOpen = false;
            secondaryClusterFields.style.display = "none";
            buttonsRow.style.display = "flex";
            buttonsRow.appendChild(submitBtn);
        }

        toggleBtn.addEventListener("click", () => openSecondary());

        setExamPrimaryFn = (wantExamPrimary: boolean) => {
            if (wantExamPrimary === examIsPrimary) return;
            examIsPrimary = wantExamPrimary;
            (examIsPrimary ? examRow : zajeciaRow).appendChild(subjectSlot);
            // replaceChildren PRZENOSI istniejące węzły (jak appendChild) - drugie wywołanie
            // "zabiera" klaster z primaryClusterHost, więc kolejność (najpierw primary, potem
            // secondary) nie ma znaczenia.
            primaryClusterHost!.replaceChildren(examIsPrimary ? examCluster : zajeciaCluster);
            secondaryClusterFields.replaceChildren(examIsPrimary ? zajeciaCluster : examCluster);
            closeSecondary();
            toggleBtn.textContent = examIsPrimary ? "Oceń też zajęcia" : "Oceń też egzamin";
        };
        setExamPrimaryFn(false);

        revealZajeciaIfHidden = () => { if (examIsPrimary) openSecondary(); };
        resetSecondaryOpenState = closeSecondary;
    } else {
        zajeciaRow.appendChild(subjectSlot);
        bottomRow.appendChild(submitBtn);
    }

    const feedback = document.createElement("p");

    // Widżet "zostaw e-mail" (patrz notifyEmail.ts) - pojawia się DOPIERO gdy komentarz trafi do
    // ręcznej moderacji (patrz submit handler niżej), nie z góry w formularzu.
    const notifyPromptContainer = document.createElement("div");

    // Pola znikają płynnie po udanym wysłaniu (patrz submit handler niżej); `feedback` chowa się
    // osobno, z opóźnieniem - oba przez tę samą sztuczkę z grid-template-rows (patrz style.css),
    // więc reszta strony przesuwa się w górę płynnie, bez przeskoku.
    // Kolejność pól: Ocena+Przedmiot, Twoja opinia, Polecasz..., Wydział (na samym końcu, tuż
    // przed przyciskiem wysyłki - zgłoszenie: pytanie o wydział miało lądować PO "Polecasz...",
    // nie między treścią opinii a nim).
    const fieldsWrapChildren: HTMLElement[] = [];
    if (primaryClusterHost) {
        fieldsWrapChildren.push(primaryClusterHost, subjectError);
    } else {
        fieldsWrapChildren.push(zajeciaRow, ratingError, subjectError, bodyFieldWrap);
        if (wouldTakeAgainField) fieldsWrapChildren.push(wouldTakeAgainField.element, wouldTakeAgainError);
    }
    if (facultyPicker) fieldsWrapChildren.push(facultyPicker.element, facultyError);
    // dualClusterWrap (gdy istnieje) zawiera już submitBtn (patrz buttonsRow) - bottomRow wtedy
    // zostaje pusty i celowo pomijany, dualClusterWrap ląduje na jego miejscu, na samym końcu,
    // jako faktyczny ostatni element formularza.
    fieldsWrapChildren.push(dualClusterWrap ?? bottomRow);
    const fieldsWrap = makeCollapsible(...fieldsWrapChildren);
    const feedbackWrap = makeCollapsible(feedback, notifyPromptContainer);
    form.append(fieldsWrap.outer, feedbackWrap.outer);

    // Timer chowający komunikat po 5s (patrz sukces niżej) - trzeba go móc anulować/zresetować,
    // gdyby ten sam formularz został użyty ponownie zanim timer wystrzeli.
    let feedbackHideTimer: number | undefined;

    // "Wyślij bez zmian" w panelu podpowiedzi (patrz showRevisionPanel niżej) - następny submit
    // idzie z confirm_unmoderated=true, czyli dawną ścieżką: zapis + ręczna moderacja.
    let confirmUnmoderated = false;
    // Ostatnia podpowiedź AI dla tego formularza (patrz showRevisionPanel) - odsyłana przy
    // następnej wysyłce, żeby backend wiedział, jak skończyło się to zdarzenie.
    let lastRevisionEventId: number | undefined;

    // Podpowiedź AI PRZED zapisem (na wyraźną prośbę 2026-10-15, patrz PostCommentView.create w
    // pages/api/comments.py): opinia nie została zapisana - autor sam decyduje, czy poprawić treść,
    // wyciąć wskazany fragment, czy wysłać bez zmian do ręcznej moderacji. Nic nie jest blokowane.
    function showRevisionPanel(revision: CommentRevisionHint, bodyValue: string): HTMLElement {
        const panel = document.createElement("div");
        panel.className = "comment-revision-panel";

        const problems = revision.problems.length > 0 ? revision.problems : [{ fragment: null, category: "", fix: "" }];
        const title = document.createElement("p");
        title.className = "comment-revision-title";
        title.textContent = problems.length === 1
            ? "Fragment Twojej opinii może być niezgodny z regulaminem:"
            : "Kilka fragmentów Twojej opinii może być niezgodnych z regulaminem:";
        panel.appendChild(title);

        const found = problems.filter(p => p.fragment && bodyValue.includes(p.fragment));
        problems.forEach(problem => {
            const item = document.createElement("div");
            item.className = "comment-revision-item";
            if (problem.fragment && bodyValue.includes(problem.fragment)) {
                const quote = document.createElement("blockquote");
                quote.className = "comment-revision-fragment";
                quote.textContent = `„${problem.fragment}”`;
                item.appendChild(quote);
            }
            // Wyjaśnienie zasady to STAŁY tekst z regulaminu (REVISION_RULES), nie słowa AI - żeby
            // było jasne, że chodzi o regulamin i RODO, a nie o to, że AI "nie spodobała się" opinia.
            const rule = document.createElement("p");
            rule.className = "comment-revision-rule";
            rule.textContent = REVISION_RULES[problem.category] ?? REVISION_RULE_FALLBACK;
            item.appendChild(rule);
            if (problem.fix) {
                const fix = document.createElement("p");
                fix.className = "comment-revision-fix";
                fix.textContent = formatRevisionFix(problem.fix);
                item.appendChild(fix);
            }
            panel.appendChild(item);
        });

        const buttons = document.createElement("div");
        buttons.className = "comment-revision-buttons";

        const editBtn = document.createElement("button");
        editBtn.type = "button";
        editBtn.className = "add-comment-btn";
        editBtn.textContent = "Popraw";
        editBtn.addEventListener("click", () => {
            panel.remove();
            bodyTextarea.focus();
            const first = found[0]?.fragment;
            if (first) {
                const start = bodyTextarea.value.indexOf(first);
                if (start >= 0) bodyTextarea.setSelectionRange(start, start + first.length);
            }
        });
        buttons.appendChild(editBtn);

        const sendAnywayBtn = document.createElement("button");
        sendAnywayBtn.type = "button";
        sendAnywayBtn.className = "comment-revision-send-anyway";
        sendAnywayBtn.textContent = "Wyślij mimo to";
        sendAnywayBtn.title = "Opinia trafi do sprawdzenia przez moderatora";
        sendAnywayBtn.addEventListener("click", () => {
            confirmUnmoderated = true;
            form.requestSubmit();
        });
        buttons.appendChild(sendAnywayBtn);

        const sendAnywayNote = document.createElement("p");
        sendAnywayNote.className = "comment-revision-note";
        // Przy zarzutach admin nie zatwierdza takich opinii (na wyraźną prośbę 2026-10-15 - nie bierze
        // odpowiedzialności za niesprawdzalne zarzuty), więc dopisek nie może obiecywać publikacji.
        sendAnywayNote.textContent = problems.some(p => p.category === "OSKARZENIE_O_NIEUCZCIWOSC")
            ? "„Wyślij mimo to” - opinia trafi do moderatora, ale pomówień, których nie da się zweryfikować, nie publikujemy. Opis tego, co się wydarzyło, możemy opublikować od razu."
            : "„Wyślij mimo to” - opinia trafi do sprawdzenia przez moderatora i zostanie opublikowana, jeśli jest zgodna z regulaminem.";

        panel.appendChild(buttons);
        panel.appendChild(sendAnywayNote);
        notifyPromptContainer.replaceChildren(panel);
        return panel;
    }

    form.addEventListener("submit", async (event: Event) => {
        event.preventDefault();
        ratingError.textContent = "";
        wouldTakeAgainError.textContent = "";
        subjectError.textContent = "";
        facultyError.textContent = "";
        bodyError.textContent = "";
        feedback.textContent = "";
        feedback.classList.remove("error");
        notifyPromptContainer.replaceChildren();
        if (feedbackHideTimer !== undefined) {
            window.clearTimeout(feedbackHideTimer);
            feedbackHideTimer = undefined;
        }
        feedbackWrap.outer.classList.remove("collapsible-collapsed");
        container.classList.remove("comment-form-collapsed");

        const bodyValue = bodyTextarea.value.trim();

        const validation = validateComment(selectedRating, bodyValue);

        if (!validation.valid) {
            if (validation.errors.rating) {
                ratingError.textContent = validation.errors.rating;
            }
            if (validation.errors.body) {
                bodyError.textContent = validation.errors.body;
            }
            // Gdy egzamin jest "primary" (patrz setExamPrimaryFn), klaster zajęć - a więc i błędy
            // wyżej - siedzi schowany za "Oceń od razu zajęcia". Bez tego user widziałby tylko
            // nieaktywny submit, bez żadnej wskazówki, co jeszcze wypełnić.
            revealZajeciaIfHidden?.();
            return;
        }

        // Gdy pole jest schowane, wouldTakeAgainField jest null i wouldTakeAgain to zawsze
        // literal null (nigdy undefined) - warunek niżej więc naturalnie nie odpala.
        const wouldTakeAgain = wouldTakeAgainField ? wouldTakeAgainField.getSelection() : null;
        if (wouldTakeAgain === undefined) {
            wouldTakeAgainError.textContent = "Zaznacz odpowiedź.";
            return;
        }

        submitBtn.disabled = true;
        feedback.textContent = "Zapisywanie...";

        let subjectId: number | null;
        let pendingSubject: Subject | undefined;
        if (!showSubjectPicker) {
            subjectId = options.fixedSubject ? options.fixedSubject.id : null;
        } else {
            // Przedmiot jest WYMAGANY (bez opcji "Komentarz ogólny" - patrz renderOptions w
            // renderSubjectPicker) - ale nie wymaga jawnego kliknięcia w podpowiedź ani w
            // "+ Dodaj przedmiot": wpisany, ale niekliknięty tekst leci tą samą ścieżką co jawne
            // kliknięcie "+" (patrz resolveSelection w renderSubjectPicker - tam mieszka cała ta
            // logika). Przy okazji doczekuje (jeśli trzeba) optymistycznie rozpoczętego tworzenia
            // nowego przedmiotu - dopiero tutaj wychodzi na jaw, czy AI je zatwierdziła, czy
            // trafiło do ręcznej moderacji.
            const resolved = await subjectPicker!.resolveSelection();
            if (resolved.revision) {
                subjectPicker!.showRevision(resolved.revision, () => form.requestSubmit());
                feedback.textContent = "";
                submitBtn.disabled = false;
                return;
            }
            if (resolved.selection === undefined) {
                subjectError.textContent = resolved.requiredFieldEmpty
                    ? "Wybierz przedmiot."
                    : "Nie udało się dodać przedmiotu - spróbuj ponownie.";
                feedback.textContent = "";
                submitBtn.disabled = false;
                return;
            }
            subjectId = resolved.selection;
            pendingSubject = resolved.pendingSubject;
        }

        // Wydział jest WYMAGANY, gdy picker w ogóle się pokazał (patrz needs_faculty_confirmation w
        // views.post_detail - picker renderuje się tylko, gdy osoba wciąż siedzi w technicznym
        // "Bez przypisanego wydziału"). Backend lustrzanie odrzuca brak wyboru w tej sytuacji
        // (patrz PostCommentView.create w comments.py) - walidacja tu tylko oszczędza roundtrip.
        let resolvedFacultyId: number | undefined = options.fixedResolvedFacultyId;
        let pendingFaculty: FacultyCandidate | undefined;
        if (facultyPicker) {
            const resolved = await facultyPicker.resolveSelection();
            if (resolved.selection === undefined) {
                facultyError.textContent = "Wybierz wydział.";
                feedback.textContent = "";
                submitBtn.disabled = false;
                return;
            }
            resolvedFacultyId = resolved.selection;
            pendingFaculty = resolved.pendingFaculty;
        }

        // Optimistic UI (patrz CommentFormOptions.onCommentConfirmed/onCommentRemoved wyżej) -
        // pokazujemy komentarz jako opublikowany OD RAZU, zamiast czekać na serwer (który m.in.
        // synchronicznie odpytuje AI moderującą treść, patrz check_comment_with_ai w
        // moderation.py - to główne źródło zauważalnego opóźnienia). Subject do wyświetlenia
        // ustalamy najlepiej jak można Z LOKALNYCH danych - dokładna wersja i tak przyjdzie zaraz
        // z serwera przy onCommentConfirmed.
        const tempId = nextOptimisticCommentId--;
        const optimisticSubject: Subject | null = options.fixedSubject !== undefined
            ? options.fixedSubject
            : pendingSubject ?? (subjectId !== null ? options.subjects.find(s => s.id === subjectId) ?? null : null);
        const optimisticComment: Comment = {
            id: tempId,
            body: bodyValue,
            created: new Date().toISOString(),
            active: true,
            rating: selectedRating,
            upvotes: 0,
            downvotes: 0,
            status: "active",
            subject: optimisticSubject,
            would_take_again: wouldTakeAgain,
            is_from_psych_import: false,
            is_from_wpia_wiki: false,
            is_from_filozofia_wiki: false,
            ai_rejection_reason: "",
            exam_rating: selectedExamRating > 0 ? selectedExamRating : null,
            exam_atmosphere: null,
            exam_difficulty: selectedExamDifficulty > 0 ? selectedExamDifficulty : null,
            exam_would_take_again: examWouldTakeAgainField ? (examWouldTakeAgainField.getSelection() ?? null) : null,
            exam_body: examBodyTextarea ? examBodyTextarea.value.trim() : "",
            class_difficulty: selectedClassDifficulty > 0 ? selectedClassDifficulty : null,
        };
        onCommentAdded(optimisticComment);
        feedback.textContent = "Dziękujemy za podzielenie się swoją opinią";
        const closedOptimistically = !confirmUnmoderated && !!options.onOptimisticClose;
        if (closedOptimistically) options.onOptimisticClose!();
        const reopenIfClosed = (): void => { if (closedOptimistically) options.onReopen?.(); };

        const result = await api.createComment(postId, {
            rating: selectedRating,
            body: bodyValue,
            status: "awaiting_moderation",
            subject_id: subjectId,
            // null, gdy pole jest schowane (patrz WOULD_TAKE_AGAIN_ENABLED) - tak samo jak
            // w starych komentarzach sprzed dodania tego pola.
            would_take_again: wouldTakeAgain,
            resolved_faculty_id: resolvedFacultyId ?? null,
            exam_rating: selectedExamRating > 0 ? selectedExamRating : null,
            exam_would_take_again: examWouldTakeAgainField ? (examWouldTakeAgainField.getSelection() ?? null) : null,
            exam_body: examBodyTextarea ? examBodyTextarea.value.trim() : "",
            class_difficulty: selectedClassDifficulty > 0 ? selectedClassDifficulty : null,
            exam_difficulty: selectedExamDifficulty > 0 ? selectedExamDifficulty : null,
            confirm_unmoderated: confirmUnmoderated || undefined,
            revision_event_id: lastRevisionEventId,
        });
        confirmUnmoderated = false;
        lastRevisionEventId = undefined;

        if (result.ok && result.data.revision) {
            // Nic nie zostało zapisane - cofamy optymistyczny wpis i pokazujemy podpowiedź.
            options.onCommentRemoved?.(tempId);
            feedback.textContent = "";
            submitBtn.disabled = false;
            lastRevisionEventId = result.data.revision.revisionEventId;
            const panel = showRevisionPanel(result.data.revision, bodyValue);
            reopenIfClosed();
            // Panel jest pod przyciskiem wysyłki - w okienku zwykle poza widokiem. Przewijamy
            // kontener okienka od razu do dołu (panel jest ostatnim elementem), w kolejnej klatce po
            // otwarciu - czyli jeszcze w trakcie animacji pojawiania się okienka (patrz
            // .add-review-dialog w style.css), więc nie widać żadnego skoku. Płynne scrollTo/
            // scrollIntoView w okienku okazały się zawodne (zatrzymywały się w połowie albo wcale).
            requestAnimationFrame(() => {
                const scroller = panel.closest("dialog");
                if (scroller) scroller.scrollTop = scroller.scrollHeight;
                else panel.scrollIntoView({ block: "end" });
            });
            return;
        }

        if (result.ok) {
            options.onSubmitted?.(result.data.comment);

            // Cele "zostaw e-mail" (komentarz + ew. przy okazji dodany przedmiot) - oba trafiają
            // do JEDNEGO widżetu (patrz renderPostHocNotifyPrompt), zamiast dwóch identycznie
            // wyglądających obok siebie (zgłoszenie: "po dodaniu komentarza renderuje się dwa
            // razy prompt") - user podaje e-mail raz, a widżet dopina go do obu zgłoszeń.
            const notifyTargets: { model: NotifyModelKey; pk: number }[] = [];

            function scheduleFullHide(): void {
                if (feedbackHideTimer !== undefined) window.clearTimeout(feedbackHideTimer);
                feedbackHideTimer = window.setTimeout(() => {
                    feedbackWrap.outer.classList.add("collapsible-collapsed");
                    container.classList.add("comment-form-collapsed");
                    options.onAutoClose?.();
                }, 3000);
            }

            if (result.data.comment.status == "active"){
                // Komentarz już jest na liście (patrz optimisticComment wyżej) - tu tylko
                // podmieniamy tymczasowy wpis na prawdziwy (prawdziwe id) i dopiero TERAZ liczą
                // się średnie/liczniki (patrz confirmComment w post_detail.ts) - feedback już
                // pokazuje "Dziękujemy" od chwili wysłania, nie trzeba go ustawiać ponownie.
                options.onCommentConfirmed?.(tempId, result.data.comment);
            }
            else if (result.data.comment.status == "awaiting_moderation"){
                // Jednak NIE aktywny - optymistyczny wpis cofamy z listy (nigdy naprawdę nie był
                // publiczny, patrz onCommentRemoved wyżej) i dopiero teraz podmieniamy "Dziękujemy"
                // na konkretne wyjaśnienie, dlaczego czeka na moderację.
                options.onCommentRemoved?.(tempId);
                // ai_rejection_reason ustawione = AI faktycznie coś wykryła w treści (w
                // odróżnieniu od awarii serwera AI, patrz Comment.ai_rejection_reason wyżej) -
                // wtedy komunikat mówi o tym wprost, zamiast sugerować zwykłą kolejkę do moderacji.
                feedback.textContent = result.data.comment.ai_rejection_reason
                    ? "Nasz system wykrył potencjalny problem z treścią komentarza, dlatego przed publikacją sprawdzi go moderator."
                    : "Komentarz został zapisany i zostanie opublikowany po sprawdzeniu przez moderację.";
                notifyTargets.push({ model: "comment", pk: result.data.comment.id });
            }

            // Przedmiot dopisany przy okazji (patrz resolveSelection wyżej) nie przeszedł bramki
            // AI - zgłaszający dowiaduje się o tym dopiero teraz, razem z wynikiem komentarza.
            if (pendingSubject) {
                feedback.textContent += ` Nowo dodany przedmiot „${pendingSubject.name}” też zostanie sprawdzony przez administratora.`;
                notifyTargets.push({ model: "subject", pk: pendingSubject.id });
            }

            // Analogicznie - nowo dodany wydział (patrz renderFacultyPicker/"+ Dodaj wydział")
            // czeka na moderację, zanim będzie widoczny publicznie (patrz FacultyListView.create).
            if (pendingFaculty) {
                feedback.textContent += ` Nowo dodany wydział „${pendingFaculty.name}” też zostanie sprawdzony przez administratora.`;
                notifyTargets.push({ model: "faculty", pk: pendingFaculty.id });
            }

            const notifyPrompts: HTMLElement[] = notifyTargets.length > 0
                ? [renderPostHocNotifyPrompt(notifyTargets, scheduleFullHide)]
                : [];

            notifyPromptContainer.replaceChildren(...notifyPrompts);
            // Komunikat o moderacji / widżet e-mail są w okienku - trzeba je pokazać.
            if (result.data.comment.status !== "active" || notifyPrompts.length > 0) reopenIfClosed();

            starRatingElement.reset();
            subjectPicker?.reset();
            wouldTakeAgainField?.reset();
            examRatingWidget?.reset();
            examWouldTakeAgainField?.reset();
            classDifficultyWidget?.reset();
            examDifficultyWidget?.reset();
            resetSecondaryOpenState?.();
            form.reset();
            // vine.bloom();

            fieldsWrap.outer.classList.add("collapsible-collapsed");
            // Gdy jest coś do zrobienia (widżet "zostaw e-mail" - patrz notifyPrompts wyżej), NIE
            // chowamy automatycznie - wcześniej znikało to po 8s nawet w trakcie wpisywania maila
            // (zgłoszenie: "mimo że wpisuję e-mail, to pole się chowa po chwili"). Tak samo jak w
            // landing2.ts/addLecturer.ts (patrz komentarz w notifyEmail.ts) - jedyny sposób, żeby
            // się go pozbyć, to podać e-mail albo kliknąć "Anuluj" (po rozwinięciu linku "tutaj").
            if (notifyPrompts.length === 0) {
                if (options.onInstantSuccess) {
                    options.onInstantSuccess(result.data.comment);
                } else {
                    feedbackHideTimer = window.setTimeout(() => {
                        feedbackWrap.outer.classList.add("collapsible-collapsed");
                        // Bez tego, po zwinięciu treści, obramowanie/padding kontenera (patrz
                        // .comment-form-box w style.css) zostawałyby puste - widoczna "ramka" bez zawartości.
                        container.classList.add("comment-form-collapsed");
                        options.onAutoClose?.();
                    }, 8000);
                }
            }
        }
        else {
            // Zapis się nie powiódł - optymistyczny wpis (pokazany od razu jako "Dziękujemy",
            // patrz optimisticComment wyżej) cofamy, nigdy naprawdę nie trafił na serwer.
            options.onCommentRemoved?.(tempId);
            // .error - ta sama czerwień co pozostałe błędy walidacji w tym formularzu (ratingError
            // itd.) - m.in. dla komunikatu limitu "Możesz dodać tylko jedną opinię do tego
            // profilu." (429 z backendu, patrz pages/api/comments.py), który wcześniej pokazywał
            // się tu w zwykłym, nieodróżniającym się kolorze tekstu.
            feedback.classList.add("error");
            feedback.textContent = `Błąd: ${result.error}`;
            reopenIfClosed();
        }
        submitBtn.disabled = false;
    });

    container.appendChild(form);

    return {
        setExamPrimary: (examPrimary: boolean) => setExamPrimaryFn?.(examPrimary),
    };
}

// "Wybrałbyś/wybrałabyś ponownie?" - prosty toggle Tak/Nie obok gwiazdek. Wynik agregowany
// w % pokazuje się potem przy ocenie (patrz Post.would_take_again_percent, showAvgRating).
function renderWouldTakeAgainField(
    label: string = "Czy polecił(a)byś innym?"
): { element: HTMLElement, getSelection: () => boolean | undefined, reset: () => void } {
    const wrapper = document.createElement("div");
    wrapper.className = "would-take-again-field";

    const labelText = document.createElement("span");
    labelText.className = "field-label-text";
    labelText.textContent = label;

    const buttonsRow = document.createElement("div");
    buttonsRow.className = "would-take-again-buttons";

    let selection: boolean | undefined = undefined;

    const yesBtn = document.createElement("button");
    yesBtn.type = "button";
    yesBtn.className = "would-take-again-btn";
    yesBtn.textContent = "Tak";

    const noBtn = document.createElement("button");
    noBtn.type = "button";
    noBtn.className = "would-take-again-btn";
    noBtn.textContent = "Nie";

    function updateActive(): void {
        yesBtn.classList.toggle("active", selection === true);
        noBtn.classList.toggle("active", selection === false);
    }

    yesBtn.addEventListener("click", () => { selection = true; updateActive(); });
    noBtn.addEventListener("click", () => { selection = false; updateActive(); });

    buttonsRow.append(yesBtn, noBtn);
    wrapper.append(labelText, buttonsRow);

    return {
        element: wrapper,
        getSelection: () => selection,
        reset: () => { selection = undefined; updateActive(); },
    };
}

// undefined = nic jeszcze nie rozwiązano - ani nie kliknięto podpowiedzi/"+", ani nie wpisano
// żadnego tekstu (patrz resolveSelection niżej: wpisany, ale niekliknięty tekst i tak zostaje
// rozwiązany przy wysyłce - to NIE jest ten przypadek). Pole jest wymagane (bez opcji "Komentarz
// ogólny" - patrz renderOptions), więc undefined przy wysyłce = błąd walidacji (patrz
// initCommentForm), nie cichy fallback na nic.
// number = id wybranego/utworzonego przedmiotu.
type SubjectSelection = number | undefined;

// Wyszukiwarka przedmiotu z możliwością dopisania nowego, gdy nie ma go na liście (jak przy
// dodawaniu wykładowcy) - własny rozwijany combobox zamiast natywnego <input list>, bo trzeba
// dołożyć akcję "Dodaj przedmiot: ...", której datalist nie obsłuży.
interface SubjectPickerResolution {
    selection: SubjectSelection;
    // Wypełniony TYLKO gdy nowo dodany przedmiot (patrz createItem niżej) nie przeszedł bramki
    // AI i czeka na ręczną moderację - komunikat o tym pokazuje się dopiero przy wysyłce
    // komentarza (patrz initCommentForm), nie od razu tutaj (patrz komentarz przy createItem).
    pendingSubject?: Subject;
    // true TYLKO gdy selection === undefined, bo pole zostało puste (nie dlatego, że tworzenie
    // nowego przedmiotu się nie powiodło) - initCommentForm używa tego, żeby pokazać właściwy
    // komunikat błędu ("Wybierz przedmiot." vs "Nie udało się dodać... - spróbuj ponownie").
    requiredFieldEmpty?: boolean;
    // AI odrzuciła wpisaną nazwę (nic nie zapisano) - initCommentForm pokazuje podpowiedź
    // (patrz showRevision niżej) zamiast wysyłać opinię.
    revision?: SubjectNameRevision;
}

// Teksty podpowiedzi dla odrzuconej nazwy przedmiotu (kategorie z check_subject_name_with_ai).
const SUBJECT_REVISION_TEXTS: Record<SubjectNameRevision["category"], string> = {
    NIE_PRZEDMIOT: "To nie wygląda na nazwę przedmiotu (np. dziekan czy kierownik studiów to funkcje). Wpisz przedmiot, na który chodziłeś/aś do tej osoby.",
    WULGARYZM: "Nazwa przedmiotu zawiera słowo, którego nie możemy opublikować.",
    NIEPRAWDZIWA_NAZWA: "To nie wygląda na nazwę przedmiotu.",
};

function renderSubjectPicker(
    postId: number,
    subjects: Subject[]
): {
    element: HTMLElement,
    resolveSelection: () => Promise<SubjectPickerResolution>,
    reset: () => void,
    showRevision: (revision: SubjectNameRevision, resubmit: () => void) => void,
} {
    const wrapper = document.createElement("div");
    wrapper.className = "subject-picker";
    const revisionSlot = document.createElement("div");

    const labelText = document.createElement("span");
    labelText.className = "field-label-text";
    // "Przedmiot, na który chodziłeś/aś" - krótka, naturalna wersja "Na jaki przedmiot z tą osobą
    // uczęszczałeś/aś?" (zgłoszenie: samo "Przedmiot" nie tłumaczyło, o co chodzi - z KIM/na
    // czyich zajęciach). Pole jest teraz WYMAGANE (na wyraźną prośbę) - bez "(opcjonalnie)" i bez
    // możliwości wyboru "Komentarz ogólny" (patrz renderOptions niżej - usunięte z kandydatów).
    labelText.textContent = "Przedmiot, na który chodziłeś/aś:";

    const searchWrapper = document.createElement("div");
    searchWrapper.className = "subject-picker-search search-with-suggestions";

    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = "Wybierz lub dodaj";
    input.autocomplete = "off";

    const dropdown = document.createElement("div");
    dropdown.className = "search-suggestions";

    let selection: SubjectSelection = undefined;
    // Ustawiane w createItem niżej - żądanie tworzące nowy przedmiot leci od razu w tle, ale
    // wybór jest OPTYMISTYCZNY (patrz tam) - resolveSelection() czeka na tę obietnicę dopiero
    // przy faktycznej wysyłce komentarza (patrz initCommentForm), nie blokując samego wyboru.
    let pendingCreation: Promise<Result<Subject | SubjectNameRevision>> | null = null;

    function closeDropdown(): void {
        dropdown.classList.remove("open");
    }

    function selectCandidate(id: number, name: string): void {
        selection = id;
        pendingCreation = null;
        input.value = name;
        closeDropdown();
    }

    function renderOptions(query: string): void {
        dropdown.replaceChildren();
        const normalizedQuery = normalizeForSearch(query);

        // BEZ "Komentarz ogólny" (na wyraźną prośbę - patrz labelText wyżej) - przedmiot jest
        // teraz wymagany, więc lista pokazuje wyłącznie realne przedmioty do wyboru.
        const candidates: { id: number, name: string }[] = subjects.map(s => ({ id: s.id, name: s.name }));

        const matches = normalizedQuery
            ? candidates.filter(c => normalizeForSearch(c.name).includes(normalizedQuery))
            : candidates;

        matches.forEach(candidate => {
            const item = document.createElement("button");
            item.type = "button";
            item.className = "search-suggestion-item";
            item.textContent = candidate.name;
            item.addEventListener("click", () => selectCandidate(candidate.id, candidate.name));
            dropdown.appendChild(item);
        });

        // Podpowiedź dodania nowego przedmiotu - tylko gdy nie ma już dokładnego dopasowania,
        // żeby nie sugerować duplikatu.
        const trimmedQuery = query.trim();
        const exactMatch = candidates.some(c => normalizeForSearch(c.name) === normalizedQuery);
        if (trimmedQuery && !exactMatch) {
            const createItem = document.createElement("button");
            createItem.type = "button";
            createItem.className = "search-suggestion-item search-suggestion-create";
            createItem.textContent = `+ Dodaj przedmiot: "${trimmedQuery}"`;
            createItem.addEventListener("click", () => {
                // Optymistycznie: NIE czekamy na odpowiedź (a więc i na sprawdzenie przez AI,
                // które potrafi trwać kilka sekund) - przedmiot jest od razu "wybrany" pod swoją
                // wpisaną nazwą, dropdown się zamyka. Prawdziwa odpowiedź (i ewentualne "czeka na
                // moderację") doczekuje się dopiero przy faktycznej wysyłce komentarza (patrz
                // resolveSelection/initCommentForm) - dopiero wtedy, nie tutaj, użytkownik
                // dowiaduje się, że przedmiot wymaga sprawdzenia.
                pendingCreation = api.createSubject(postId, trimmedQuery);
                selection = undefined;
                input.value = trimmedQuery;
                closeDropdown();
            });
            dropdown.appendChild(createItem);
        }

        // ZAWSZE na dole listy (nie tylko gdy wydział nie ma jeszcze żadnego przedmiotu) - lista
        // istniejących przedmiotów sama z siebie nie mówi, że wpisanie czegokolwiek spoza niej też
        // doda nowy przedmiot (zgłoszenie: brak tej podpowiedzi budził wątpliwość). Tylko przy
        // pustym zapytaniu - gdy user już coś wpisuje, ten sam komunikat leci przez
        // "+ Dodaj przedmiot: ..." wyżej (patrz createItem), więc tu byłby zbędnym powtórzeniem.
        if (!trimmedQuery) {
            const hint = document.createElement("p");
            hint.className = "search-suggestion-hint";
            hint.textContent = "Zacznij pisać, by dodać nowy przedmiot.";
            dropdown.appendChild(hint);
        }

        if (dropdown.children.length > 0) {
            dropdown.classList.add("open");
        } else {
            closeDropdown();
        }
    }

    input.addEventListener("input", () => {
        // każda zmiana tekstu unieważnia poprzedni wybór (w tym niedokończone optymistyczne
        // tworzenie przedmiotu, patrz pendingCreation) - trzeba wybrać ponownie
        selection = undefined;
        pendingCreation = null;
        revisionSlot.replaceChildren();
        renderOptions(input.value);
    });

    input.addEventListener("focus", () => {
        renderOptions(input.value);
    });

    // Bez tego kliknięcie/tap w pozycję listy przenosiłoby fokus z inputu na klikany <button>
    // (domyślne zachowanie mousedown na fokusowalnym elemencie) - input dostawałby "focusout"
    // ZANIM zdąży się zarejestrować "click" niżej, a na przeglądarkach, które nie fokusują
    // przycisków myszą/dotykiem (Safari/iOS - tam fokus w ogóle nie trafia na button), dropdown
    // zamykał się (patrz focusout niżej) zanim "click" w ogóle dotarł do przeglądarki (opóźniony
    // przez natywną detekcję podwójnego tapnięcia) - zgłoszenie: "kliknięcie nic nie robi, działają
    // tylko strzałki + Enter" (te idą inną ścieżką - patrz attachDropdownKeyboardNav niżej, wcale
    // nie dotykają fokusu myszy). preventDefault na mousedown blokuje WYŁĄCZNIE to domyślne
    // przenoszenie fokusu - samo zdarzenie "click" (na mouseup) leci dalej normalnie i to ono
    // wybiera pozycję (patrz item.addEventListener("click", ...) w renderOptions wyżej). Fokus
    // zostaje więc przez cały czas na inpucie - "focusout" (patrz niżej) już nigdy nie odpala się
    // przez samo kliknięcie w listę, tylko przez faktyczną zmianę fokusu (Tab/Shift+Tab).
    dropdown.addEventListener("mousedown", (event) => {
        event.preventDefault();
    });

    // Tab/Shift+Tab przenosi fokus bez klikania - focusout łapie to. setTimeout 0 zostaje jako
    // zabezpieczenie (na wypadek fokusu, który realnie ląduje gdzieś w searchWrapper w tym samym
    // ticku), ale dzięki preventDefault wyżej nie musi już chronić przed kliknięciem w pozycję
    // listy - to zdarzenie tu w ogóle nie dociera.
    searchWrapper.addEventListener("focusout", () => {
        window.setTimeout(() => {
            if (!searchWrapper.contains(document.activeElement)) {
                closeDropdown();
            }
        }, 0);
    });

    document.addEventListener("click", (event) => {
        if (!searchWrapper.contains(event.target as Node)) {
            closeDropdown();
        }
    });

    attachDropdownKeyboardNav(input, dropdown);

    searchWrapper.append(input, dropdown);
    wrapper.append(labelText, searchWrapper, revisionSlot);

    // Wspólna ścieżka dla "kliknięto + Dodaj przedmiot" (pendingCreation, patrz createItem wyżej)
    // i dla "wpisano tekst, ale nic nie kliknięto" (patrz resolveSelection niżej) - obie kończą
    // się identycznie: czekamy na odpowiedź API i ustawiamy selection na jej podstawie; gdy
    // przedmiot nie przeszedł bramki AI, zwracamy go jako pendingSubject do komunikatu o
    // moderacji (patrz initCommentForm).
    async function resolveCreation(promise: Promise<Result<Subject | SubjectNameRevision>>): Promise<SubjectPickerResolution> {
        const res = await promise;
        pendingCreation = null;
        if (!res.ok) {
            selection = undefined;
            return { selection };
        }
        if ("needs_revision" in res.data) {
            selection = undefined;
            return { selection, revision: res.data };
        }
        selection = res.data.id;
        if (res.data.status !== "active") {
            // AI zablokowała nazwę - przedmiot NIE jest jeszcze wybieralny publicznie (nie wraca
            // z API, dopóki administrator go nie zatwierdzi - patrz SubjectListView.get_queryset).
            // Zgłaszający dowiaduje się o tym dopiero teraz, przy wysyłce komentarza (patrz
            // initCommentForm) - subject_id i tak wskazuje na ten (na razie oczekujący) rekord.
            return { selection, pendingSubject: res.data };
        }
        const created = res.data;
        if (!subjects.some(s => s.id === created.id)) {
            subjects.push(created);
        }
        return { selection };
    }

    // Wybór po podpowiedzi: istniejący przedmiot o tej nazwie albo utworzenie nowego.
    function chooseName(name: string, confirmModeration = false): void {
        const existing = subjects.find(s => normalizeForSearch(s.name) === normalizeForSearch(name));
        if (existing && !confirmModeration) {
            selectCandidate(existing.id, existing.name);
            return;
        }
        input.value = name;
        selection = undefined;
        pendingCreation = api.createSubject(postId, name, confirmModeration);
    }

    // Druga szansa po odrzuceniu nazwy przez AI (ten sam wygląd co podpowiedź do treści opinii).
    function showRevision(revision: SubjectNameRevision, resubmit: () => void): void {
        const panel = document.createElement("div");
        panel.className = "comment-revision-panel subject-revision-panel";

        const title = document.createElement("p");
        title.className = "comment-revision-title";
        // Z propozycją poprawki (i bez wulgaryzmu) to zwykle literówka - łagodniejszy tekst.
        const looksLikeTypo = revision.suggested_name && revision.category !== "WULGARYZM";
        title.textContent = looksLikeTypo
            ? `„${revision.name}”: możliwa literówka w nazwie przedmiotu.`
            : `„${revision.name}”: ${SUBJECT_REVISION_TEXTS[revision.category] ?? SUBJECT_REVISION_TEXTS.NIEPRAWDZIWA_NAZWA}`;
        panel.appendChild(title);

        const buttons = document.createElement("div");
        buttons.className = "comment-revision-buttons";

        if (revision.suggested_name) {
            const suggestBtn = document.createElement("button");
            suggestBtn.type = "button";
            suggestBtn.className = "add-comment-btn";
            suggestBtn.textContent = `Czy chodziło o: ${revision.suggested_name}?`;
            suggestBtn.addEventListener("click", () => {
                revisionSlot.replaceChildren();
                chooseName(revision.suggested_name);
                resubmit();
            });
            buttons.appendChild(suggestBtn);
        }

        const editBtn = document.createElement("button");
        editBtn.type = "button";
        editBtn.className = revision.suggested_name ? "comment-revision-send-anyway" : "add-comment-btn";
        editBtn.textContent = "Popraw nazwę";
        editBtn.addEventListener("click", () => {
            revisionSlot.replaceChildren();
            input.focus();
            input.select();
        });

        buttons.append(editBtn);
        panel.appendChild(buttons);

        // <a>, nie <button> - ogólna reguła dla przycisków formularza nadałaby mu wygląd głównego przycisku.
        const confirmLink = document.createElement("a");
        confirmLink.href = "#";
        confirmLink.className = "comment-revision-note subject-revision-confirm";
        confirmLink.textContent = "To prawdziwy przedmiot - wyślij go do sprawdzenia przez moderację";
        confirmLink.addEventListener("click", (event) => {
            event.preventDefault();
            revisionSlot.replaceChildren();
            chooseName(revision.name, true);
            resubmit();
        });
        panel.appendChild(confirmLink);

        revisionSlot.replaceChildren(panel);
    }

    return {
        element: wrapper,
        resolveSelection: async () => {
            if (pendingCreation) {
                return resolveCreation(pendingCreation);
            }
            // Nic nie kliknięto (ani podpowiedź z listy, ani "+ Dodaj przedmiot") - pole jest
            // WYMAGANE (bez "Komentarz ogólny" - patrz renderOptions), więc puste pole zostaje
            // undefined (initCommentForm zgłasza to jako błąd walidacji). Niepusty, wpisany-ale-
            // niekliknięty tekst leci tą samą ścieżką co jawne kliknięcie "+ Dodaj przedmiot"
            // (patrz createItem wyżej / resolveCreation wyżej) - to wciąż zostaje, żeby nie trzeba
            // było klikać czegokolwiek po samym wpisaniu istniejącej/nowej nazwy.
            if (selection === undefined) {
                const trimmed = input.value.trim();
                if (!trimmed) {
                    return { selection: undefined, requiredFieldEmpty: true };
                }
                return resolveCreation(api.createSubject(postId, trimmed));
            }
            return { selection };
        },
        reset: () => {
            selection = undefined;
            pendingCreation = null;
            input.value = "";
            revisionSlot.replaceChildren();
            closeDropdown();
        },
        showRevision,
    };
}

interface FacultyPickerResolution {
    // undefined = nic nie wybrano/wpisano - pole jest WYMAGANE (patrz initCommentForm - submit
    // handler blokuje wysyłkę i pokazuje facultyError, gdy to zostaje undefined).
    selection: number | undefined;
    pendingFaculty?: FacultyCandidate;
}

// Wyszukiwarka wydziału z możliwością dopisania nowego - ten sam wzorzec co renderSubjectPicker
// wyżej (własny combobox + "+ Dodaj wydział: ..."), z dwiema różnicami: (1) `candidates` startuje
// pusta (patrz candidate_faculties w views.post_detail - pokazuje się tylko, gdy osoba wciąż
// siedzi w technicznym "Bez przypisanego wydziału") i dociąga W TLE pełną listę wydziałów tej
// uczelni (patrz fetch niżej) - to jedyne źródło podpowiedzi; (2) wpisany-ale-niekliknięty tekst
// NIE tworzy automatycznie nowego wydziału przy wysyłce (w odróżnieniu od przedmiotu) - założenie
// nowego wydziału to poważniejszy krok niż przedmiotu, więc wymaga jawnego kliknięcia
// "+ Dodaj wydział". Pole jest WYMAGANE (patrz FacultyPickerResolution) - w odróżnieniu od
// przedmiotu nie ma tu "Komentarz ogólny"/pustej opcji, bo to właśnie ten formularz jest jedynym
// źródłem, z którego wydział tej osoby w ogóle się ustala (patrz PostCommentView._resolve_faculty).
function renderFacultyPicker(
    universitySlug: string,
    candidates: FacultyCandidate[],
    preselectSlug: string | null
): { element: HTMLElement, resolveSelection: () => Promise<FacultyPickerResolution> } {
    const wrapper = document.createElement("div");
    wrapper.className = "subject-picker faculty-picker";

    const labelText = document.createElement("span");
    labelText.className = "field-label-text";
    labelText.textContent = "Z którego wydziału jest ta osoba?";

    const searchWrapper = document.createElement("div");
    searchWrapper.className = "subject-picker-search search-with-suggestions";

    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = "Wybierz...";
    input.autocomplete = "off";

    const dropdown = document.createElement("div");
    dropdown.className = "search-suggestions";

    // Kandydaci rosną w tle o pełną listę wydziałów uczelni (patrz fetch niżej) - kopia, żeby nie
    // mutować tablicy przekazanej z zewnątrz.
    let allCandidates: FacultyCandidate[] = [...candidates];
    let selection: number | undefined = undefined;
    let pendingCreation: Promise<Result<FacultyCandidate>> | null = null;

    // Dociąga w tle PEŁNĄ listę wydziałów tej uczelni - `candidates` startuje puste (patrz
    // PLACEHOLDER_FACULTY_SLUG w views.post_detail), więc to jedyne źródło podpowiedzi. Dopisuje
    // się do dropdownu, jeśli jest akurat otwarty (żeby nie trzeba było klikać poza pole i z
    // powrotem, gdyby zapytanie odpowiedziało póżno).
    api.getFaculties(universitySlug).then(res => {
        if (!res.ok) return;
        const known = new Set(allCandidates.map(c => c.id));
        const extra = res.data.faculties.filter(f => f.slug !== PLACEHOLDER_FACULTY_SLUG && !known.has(f.id));
        allCandidates = [...allCandidates, ...extra];
        // Prefill z nawigacji (patrz preselectSlug/?wydzial= w post_detail.ts) - `candidates`
        // startuje puste (patrz komentarz wyżej), więc dopóki ta lista nie dociągnie się w tle,
        // nie ma wśród czego szukać; stąd preselect dzieje się TU, nie od razu przy renderowaniu
        // pickera. `selection === undefined` - nie nadpisujemy, jeśli user już zdążył coś wybrać
        // zanim to zapytanie wróciło.
        if (preselectSlug && selection === undefined) {
            const preselected = allCandidates.find(c => c.slug === preselectSlug);
            if (preselected) selectCandidate(preselected.id, preselected.name);
        }
        if (dropdown.classList.contains("open")) renderOptions(input.value);
    });

    function closeDropdown(): void {
        dropdown.classList.remove("open");
    }

    function selectCandidate(id: number, name: string): void {
        selection = id;
        pendingCreation = null;
        input.value = name;
        closeDropdown();
    }

    function renderOptions(query: string): void {
        dropdown.replaceChildren();
        const normalizedQuery = normalizeForSearch(query);

        const matches = normalizedQuery
            ? allCandidates.filter(c => normalizeForSearch(c.name).includes(normalizedQuery))
            : allCandidates;

        matches.forEach(candidate => {
            const item = document.createElement("button");
            item.type = "button";
            item.className = "search-suggestion-item";
            item.textContent = candidate.name;
            item.addEventListener("click", () => selectCandidate(candidate.id, candidate.name));
            dropdown.appendChild(item);
        });

        const trimmedQuery = query.trim();
        const exactMatch = allCandidates.some(c => normalizeForSearch(c.name) === normalizedQuery);
        if (trimmedQuery && !exactMatch) {
            const createItem = document.createElement("button");
            createItem.type = "button";
            createItem.className = "search-suggestion-item search-suggestion-create";
            createItem.textContent = `+ Dodaj wydział: "${trimmedQuery}"`;
            createItem.addEventListener("click", () => {
                pendingCreation = api.createFaculty(trimmedQuery, universitySlug);
                selection = undefined;
                input.value = trimmedQuery;
                closeDropdown();
            });
            dropdown.appendChild(createItem);
        }

        if (dropdown.children.length > 0) {
            dropdown.classList.add("open");
        } else {
            closeDropdown();
        }
    }

    input.addEventListener("input", () => {
        selection = undefined;
        pendingCreation = null;
        renderOptions(input.value);
    });

    input.addEventListener("focus", () => renderOptions(input.value));

    document.addEventListener("click", (event) => {
        if (!searchWrapper.contains(event.target as Node)) {
            closeDropdown();
        }
    });

    attachDropdownKeyboardNav(input, dropdown);

    searchWrapper.append(input, dropdown);
    wrapper.append(labelText, searchWrapper);

    async function resolveCreation(promise: Promise<Result<FacultyCandidate>>): Promise<FacultyPickerResolution> {
        const res = await promise;
        pendingCreation = null;
        if (!res.ok) {
            selection = undefined;
            return { selection };
        }
        selection = res.data.id;
        // Nowo dodany wydział czeka na moderację (patrz FacultyListView.create) - nie pojawia się
        // publicznie, dopóki administrator go nie zatwierdzi, ale post.faculty i tak może już na
        // niego wskazywać (patrz _resolve_faculty w comments.py) - to nie blokuje niczego innego.
        return { selection, pendingFaculty: res.data };
    }

    return {
        element: wrapper,
        resolveSelection: async () => {
            if (pendingCreation) {
                return resolveCreation(pendingCreation);
            }
            return { selection };
        },
    };
}

export function validateComment(rating_int: number, body: string): ValidationResult {
    const errors: Record<string, string> = {};

    if ( rating_int < 1 || rating_int > 5) {
        errors.rating = "Ocena musi być z zakresu 0-5";
    }
    if (body.length < 5 || body.length > COMMENT_BODY_MAX_LENGTH) {
        errors.body = "Komentarz musi mieć co najmniej 5 i co najwyżej 500 znaków";
    }

    if (Object.keys(errors).length > 0) {
        return { valid: false, errors };
    }

    return { valid: true };
}

export function sortComments(comments: Comment[], by: CommentSortField): Comment[] {
    const sorted = [...comments];

    sorted.sort((a, b) => {
        switch (by) {
            case "newest":
                return b.created.localeCompare(a.created);
            case "oldest":
                return a.created.localeCompare(b.created);
            // (?? 0) - opinie egzamin-only (patrz rating w interfejsie Comment wyżej) nie mają
            // zajęciowej oceny - traktowane jak najniższa możliwa przy tym sortowaniu, zamiast
            // wywalać się na arytmetyce z null.
            case "best_rating":
                return (b.rating ?? 0) - (a.rating ?? 0);
            case "worst_rating":
                return (a.rating ?? 0) - (b.rating ?? 0);
            case "best_exam_rating":
                return (b.exam_rating ?? 0) - (a.exam_rating ?? 0);
            case "worst_exam_rating":
                return (a.exam_rating ?? 0) - (b.exam_rating ?? 0);
            case "most_upvotes":
                if (b.upvotes - a.upvotes != 0){
                    return b.upvotes - a.upvotes;
                } else {
                    return b.created.localeCompare(a.created);
                }
            default:
                return 0;
        }
    });

    return sorted;
}

// Grupuje komentarze wg przedmiotu. "Komentarz ogólny" (subject === null) rządzi się tymi samymi
// zasadami co pozostałe grupy - wszystkie sortowane wg tego, gdzie pojawił się najnowszy komentarz
// (malejąco), więc dawno "nietknięta" grupa (w tym Ogólny) zjeżdża na dół. Grupa Ogólny jest
// zawsze obecna (nawet pusta), żeby przycisk "+" był zawsze dostępny.
export function groupCommentsBySubject(comments: Comment[]): CommentGroup[] {
    const groupsBySubjectId = new Map<number, CommentGroup>();
    const generalGroup: CommentGroup = { subject: null, comments: [] };

    for (const comment of comments) {
        if (comment.subject === null) {
            generalGroup.comments.push(comment);
            continue;
        }
        const existing = groupsBySubjectId.get(comment.subject.id);
        if (existing) {
            existing.comments.push(comment);
        } else {
            groupsBySubjectId.set(comment.subject.id, { subject: comment.subject, comments: [comment] });
        }
    }

    const mostRecentCreated = (group: CommentGroup): string =>
        group.comments.reduce((latest, c) => (c.created > latest ? c.created : latest), "");

    const allGroups = [generalGroup, ...groupsBySubjectId.values()];

    return allGroups.sort((a, b) => mostRecentCreated(b).localeCompare(mostRecentCreated(a)));
}

// Sortuje listę przedmiotów tak samo jak grupy w widoku zgrupowanym wyżej - wg tego, gdzie
// pojawił się najnowszy komentarz (malejąco), a NIE alfabetycznie. Używane w wyszukiwarce
// przedmiotu przy dodawaniu opinii (patrz renderSubjectPicker), żeby najbardziej "żywe" (aktywne)
// przedmioty były widoczne od razu, zanim użytkownik zacznie cokolwiek wpisywać. Przedmioty bez
// żadnego komentarza (jeszcze) lądują na końcu, w kolejności, w jakiej przyszły z API.
export function sortSubjectsByRecentActivity(subjects: Subject[], comments: Comment[]): Subject[] {
    const mostRecentBySubjectId = new Map<number, string>();
    for (const comment of comments) {
        if (comment.subject === null) continue;
        const current = mostRecentBySubjectId.get(comment.subject.id) ?? "";
        if (comment.created > current) {
            mostRecentBySubjectId.set(comment.subject.id, comment.created);
        }
    }

    return [...subjects].sort((a, b) => {
        const aDate = mostRecentBySubjectId.get(a.id) ?? "";
        const bDate = mostRecentBySubjectId.get(b.id) ?? "";
        return bDate.localeCompare(aDate);
    });
}

// labels - domyślnie STAR_RATING_LABELS (zajęcia), ale ten sam widget służy też ocenie egzaminu
// (patrz initCommentForm - EXAM_RATING_LABELS z generics.ts, "Wybitny" nie pasuje do egzaminu).
export function renderStarRating(
    onRatingChange: (rating: number) => void,
    labels: { [key: number]: string } = STAR_RATING_LABELS
): {element: HTMLElement, reset: ()=>void} {
    const wrapper = document.createElement("div");
    wrapper.className = "star-rating-widget";

    const container = document.createElement("div");
    container.className = "star-rating";
    container.setAttribute("role", "radiogroup");
    container.setAttribute("aria-label", "Ocena w gwiazdkach");

    // Etykieta pod gwiazdkami (Wybitny/Świetny/...) - pokazuje się tylko pod tą gwiazdką, na
    // którą się właśnie najeżdża myszką; po kliknięciu (wybraniu oceny) i po zjechaniu myszką
    // znika - sama ocena jest już widoczna w podświetleniu gwiazdek. Statyczna podpowiedź
    // "Kliknij, aby ocenić" (pokazywana tu wcześniej, dopóki nic nie wybrano) usunięta na wyraźną
    // prośbę - etykieta "Ocena:" obok gwiazdek (patrz initCommentForm) wystarcza.
    const hoverLabel = document.createElement("span");
    hoverLabel.className = "star-rating-hover-label";

    var currentRating = 0;
    const stars: HTMLButtonElement[] = [];

    const starLabel = (count: number) => `${count} ${count === 1 ? "gwiazdka" : "gwiazdki"} - ${labels[count]}`;

    // Funkcja pomocnicza do kolorowania gwiazdek
    const highlightStars = (count: number) => {
        stars.forEach((s, index) => {
            if (index < count) {
                s.classList.add("active");
            } else {
                s.classList.remove("active");
            }
        });
    };

    const updateHoverLabel = (value: number) => {
        hoverLabel.textContent = value > 0 ? labels[value] : "";
    };

    // Na telefonie nie ma hover - "mouseenter" niżej nigdy się nie odpala, więc etykieta bez
    // tego nigdy nie byłaby widoczna po dotyku. Klik (też ten "syntetyczny" z tapnięcia) pokazuje
    // etykietę na chwilę, potem sama znika - jak na desktopie po zjechaniu myszką. Zegarek trzeba
    // kasować przy KAŻDYM najechaniu/zjechaniu myszką (nie tylko przy kolejnym kliku) - inaczej
    // spóźniony timeout z wcześniejszego klika mógłby wyzerować etykietę aktualnie najechanej
    // (inną) gwiazdki na desktopie.
    let hoverLabelHideTimeout: ReturnType<typeof setTimeout> | undefined;
    const clearHoverLabelHideTimeout = () => {
        if (hoverLabelHideTimeout !== undefined) {
            clearTimeout(hoverLabelHideTimeout);
            hoverLabelHideTimeout = undefined;
        }
    };
    const showHoverLabelBriefly = (value: number) => {
        clearHoverLabelHideTimeout();
        updateHoverLabel(value);
        hoverLabelHideTimeout = setTimeout(() => {
            hoverLabelHideTimeout = undefined;
            updateHoverLabel(0);
        }, 1200);
    };

    for (let i = 1; i <= 5; i++) {
        const star = document.createElement("button");
        star.type = "button";
        star.className = "star";
        star.classList.add("current");
        star.innerHTML = "&#9733;";
        star.dataset.value = String(i);
        star.setAttribute("role", "radio");
        star.setAttribute("aria-checked", "false");
        star.setAttribute("aria-label", starLabel(i));

        // 1. Ewent: Najechanie myszką (podświetlanie "w locie" + etykieta oceny)
        star.addEventListener("mouseenter", () => {
            clearHoverLabelHideTimeout();
            highlightStars(i);
            updateHoverLabel(i);
        });

        // 2. Event: Kliknięcie / aktywacja z klawiatury (zapisanie oceny) - na desktopie etykieta
        // jest już widoczna od "mouseenter" wyżej, więc pokazanie jej tu jeszcze raz nic nie
        // zmienia; na telefonie (bez hover) to JEDYNY moment, w którym w ogóle się pojawia - stąd
        // showHoverLabelBriefly (sama znika po chwili), a nie stary, natychmiastowy updateHoverLabel(0).
        star.addEventListener("click", () => {
            currentRating = i;
            highlightStars(i);
            showHoverLabelBriefly(i);
            stars.forEach((s, index) => s.setAttribute("aria-checked", String(index === i - 1)));
            onRatingChange(currentRating); // Przekazujemy ocenę dalej
        });

        stars.push(star);
        container.appendChild(star);
    }

    // 3. Event: Zjechanie myszką z całego kontenera (powrót do zapisanej oceny, bez etykiety).
    // WAŻNE: telefony syntetyzują pełny ciąg zdarzeń myszy PO tapnięciu (touchstart/touchend,
    // potem mouseenter/mousedown/mouseup/click, na końcu mouseout/mouseleave - tak realne
    // przeglądarki mobilne, jak i emulacja dotyku w Chromium/CDP) - bez tej strażniczki to
    // sztuczne "zjechanie myszką" natychmiast kasowało etykietę, którą showHoverLabelBriefly
    // dopiero co pokazał w click() wyżej, więc na telefonie w ogóle nie było jej widać. Gdy
    // hoverLabelHideTimeout już czeka (czyli klik/tap właśnie pokazał etykietę na chwilę),
    // zostawiamy jej naturalne wygaśnięcie zamiast przerywać je tym zdarzeniem.
    container.addEventListener("mouseleave", () => {
        highlightStars(currentRating);
        if (hoverLabelHideTimeout === undefined) {
            updateHoverLabel(0);
        }
    });

    const reset = () => {
        currentRating = 0;
        onRatingChange(0); // Powiadamiamy formularz że teraz wartość to 0
        clearHoverLabelHideTimeout();
        stars.forEach(s => {
            s.classList.remove("active");
            s.setAttribute("aria-checked", "false");
        });
        updateHoverLabel(0);
    }

    wrapper.append(container, hoverLabel);

    return { element: wrapper, reset: reset };
}

    

    